import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha, styled } from '@mui/material/styles';
import { FileText, Search, AlertTriangle, Mail, XCircle, HelpCircle, ChevronDown, ChevronUp, Settings2, Upload, Trash2, Send, CheckCircle2, BellRing, XOctagon, User, Phone, ExternalLink, MoreVertical, Pencil, Check, X, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
};

const guessMimeFromFilename = (filename) => {
  const name = String(filename || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  return 'application/octet-stream';
};

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const StatWidget = ({ title, value, icon: Icon, color, active, onClick }) => (
  <Paper
    sx={(theme) => ({
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      overflow: 'hidden',
      minWidth: 0,
      borderRadius: '16px',
      cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none',
      borderColor: active ? color : theme.palette.divider,
      bgcolor: active ? alpha(color, 0.06) : theme.palette.background.paper,
      transition: 'background-color 120ms ease, border-color 120ms ease',
      '&:hover': onClick ? { bgcolor: 'rgba(0,0,0,0.02)' } : undefined
    })}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.25,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        mr: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={20} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const richiestaDocStatusMeta = (stato) => {
  const s = String(stato || '').trim().toLowerCase();
  if (s === 'predisposta') return { label: 'Predisposta', Icon: AlertTriangle, color: '#f59e0b', bg: '#f59e0b1A' };
  if (s === 'inviata') return { label: 'Inviata', Icon: Mail, color: '#2563eb', bg: '#2563eb1A' };
  if (s === 'non_gestire') return { label: 'Non gestire', Icon: XCircle, color: '#64748b', bg: '#64748b1A' };
  return { label: 'Non impostata', Icon: HelpCircle, color: '#ef4444', bg: '#ef44441A' };
};

const detectRichiestaDocTypeFromName = (filename) => {
  const name = String(filename || '').toLowerCase();
  if (!name) return null;
  if (name.includes('contratto')) return 'contratto';
  if (name.includes('mandato')) return 'mandato';
  if (name.includes('questionario')) return 'questionario';
  if (name.includes('visura')) return 'visura';
  if (name.includes('doc') && (name.includes('id') || name.includes('ident') || name.includes('carta') || name.includes('ci'))) {
    return 'doc_identita';
  }
  if (name.includes('ident') || name.includes('carta') || name.includes('ci')) return 'doc_identita';
  return null;
};

const richiestaDocTypeLabel = (type) => {
  if (type === 'contratto') return 'Contratto';
  if (type === 'mandato') return 'Mandato';
  if (type === 'doc_identita') return 'Doc identità';
  if (type === 'visura') return 'Visura Azienda';
  if (type === 'questionario') return 'Questionario';
  return type;
};

const getCustomerDisplayName = (dossier) => {
  if (!dossier) return '-';
  if (dossier.customer_ragione_sociale) return dossier.customer_ragione_sociale;
  const fullName = [dossier.customer_name, dossier.customer_surname].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return '-';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatTimeAgo = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const diffSeconds = Math.round((Date.now() - d.getTime()) / 1000);
  const rtf =
    typeof Intl !== 'undefined' && typeof Intl.RelativeTimeFormat === 'function'
      ? new Intl.RelativeTimeFormat('it', { numeric: 'auto' })
      : null;

  const abs = Math.abs(diffSeconds);
  const itUnit = (unit, amount) => {
    const n = Math.abs(Number(amount) || 0);
    if (unit === 'second') return n === 1 ? 'secondo' : 'secondi';
    if (unit === 'minute') return n === 1 ? 'minuto' : 'minuti';
    if (unit === 'hour') return n === 1 ? 'ora' : 'ore';
    if (unit === 'day') return n === 1 ? 'giorno' : 'giorni';
    if (unit === 'month') return n === 1 ? 'mese' : 'mesi';
    if (unit === 'year') return n === 1 ? 'anno' : 'anni';
    return unit;
  };
  const pick = (unit, secondsPerUnit) => {
    const v = Math.floor(abs / secondsPerUnit);
    if (rtf) return rtf.format(diffSeconds < 0 ? v : -v, unit);
    if (diffSeconds < 0) return `tra ${v} ${itUnit(unit, v)}`;
    return `${v} ${itUnit(unit, v)} fa`;
  };

  if (abs < 45) return rtf ? rtf.format(0, 'second') : 'adesso';
  if (abs < 60) return pick('second', 1);
  if (abs < 60 * 60) return pick('minute', 60);
  if (abs < 60 * 60 * 24) return pick('hour', 60 * 60);
  if (abs < 60 * 60 * 24 * 30) return pick('day', 60 * 60 * 24);
  if (abs < 60 * 60 * 24 * 365) return pick('month', 60 * 60 * 24 * 30);
  return pick('year', 60 * 60 * 24 * 365);
};

const toValidDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const daysDiff = (fromValue, toValue) => {
  const from = toValidDate(fromValue);
  const to = toValidDate(toValue);
  if (!from || !to) return null;
  const a = new Date(from);
  const b = new Date(to);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const diff = b.getTime() - a.getTime();
  return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
};

const formatDaysIt = (days) => (days === 1 ? '1 giorno' : `${days} giorni`);

const formatPredispostaDa = (createdAt) => {
  const days = daysDiff(createdAt, Date.now());
  if (days == null) return '-';
  if (days === 0) return 'da oggi';
  return `da ${formatDaysIt(days)}`;
};

const formatGestitaIn = (createdAt, inviataIl) => {
  const days = daysDiff(createdAt, inviataIl);
  if (days == null) return '-';
  if (days === 0) return 'Gestita in giornata';
  return `Gestita in ${formatDaysIt(days)}`;
};

const formatUserName = (user) => {
  if (!user) return '-';
  const full = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return full || '-';
};

const quillModules = {
  toolbar: [
    [{ 'font': [] }, { 'size': [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'link', 'image'
];

const issuerItemStatusMeta = (stato) => {
  const s = String(stato || '').trim().toLowerCase();
  if (s === 'ricevuta') return { label: 'Ricevuta', Icon: CheckCircle2, color: '#16a34a', bg: '#16a34a1A' };
  if (s === 'inviata') return { label: 'Inviata', Icon: Send, color: '#2563eb', bg: '#2563eb1A' };
  if (s === 'inviata_manualmente') return { label: 'Inviata (manuale)', Icon: Pencil, color: '#0ea5e9', bg: '#0ea5e91A' };
  if (s === 'sollecito') return { label: 'Sollecito', Icon: BellRing, color: '#f59e0b', bg: '#f59e0b1A' };
  if (s === 'errore') return { label: 'Errore', Icon: XOctagon, color: '#dc2626', bg: '#dc26261A' };
  if (s === 'non_gestire') return { label: 'Non gestire', Icon: XCircle, color: '#64748b', bg: '#64748b1A' };
  return { label: 'Predisposta', Icon: AlertTriangle, color: '#f59e0b', bg: '#f59e0b1A' };
};

export default function RichiesteDocumenti() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  const [search, setSearch] = useState('');
  const [stato, setStato] = useState('all');
  const [diffidaFilter, setDiffidaFilter] = useState('all');
  const [stats, setStats] = useState({
    predisposta: 0,
    pec_inviata_parziale: 0,
    inviata: 0,
    pec_senza_risposta: 0,
    non_gestire: 0,
    diffida_inviata: 0,
    diffida_da_inviare: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const [expanded, setExpanded] = useState({});
  const [issuerExpanded, setIssuerExpanded] = useState({});
  const [issuerRows, setIssuerRows] = useState({});
  const [issuerLoading, setIssuerLoading] = useState({});
  const [issuerItemMenuAnchorEl, setIssuerItemMenuAnchorEl] = useState(null);
  const [issuerItemMenuCtx, setIssuerItemMenuCtx] = useState(null);
  const [issuerItemStatoSaving, setIssuerItemStatoSaving] = useState(false);
  const [issuerItemStatoSavingId, setIssuerItemStatoSavingId] = useState(null);
  const [issuerItemInvioEditId, setIssuerItemInvioEditId] = useState(null);
  const [issuerItemInvioEditValue, setIssuerItemInvioEditValue] = useState('');
  const [issuerItemInvioEditOriginal, setIssuerItemInvioEditOriginal] = useState('');
  const [issuerItemInvioSavingId, setIssuerItemInvioSavingId] = useState(null);
  const [issuerItemDiffidaEditId, setIssuerItemDiffidaEditId] = useState(null);
  const [issuerItemDiffidaEditValue, setIssuerItemDiffidaEditValue] = useState('');
  const [issuerItemDiffidaEditOriginal, setIssuerItemDiffidaEditOriginal] = useState('');
  const [issuerItemDiffidaSavingId, setIssuerItemDiffidaSavingId] = useState(null);
  const [addIssuerDrawerOpen, setAddIssuerDrawerOpen] = useState(false);
  const [addIssuerCtx, setAddIssuerCtx] = useState(null);
  const [addIssuerQuery, setAddIssuerQuery] = useState('');
  const [addIssuerOptions, setAddIssuerOptions] = useState([]);
  const [addIssuerLoading, setAddIssuerLoading] = useState(false);
  const [addIssuerError, setAddIssuerError] = useState('');
  const [addIssuerSelected, setAddIssuerSelected] = useState(null);
  const [addIssuerSaving, setAddIssuerSaving] = useState(false);

  const [risposteTipi, setRisposteTipi] = useState([]);
  const [risposteTipiLoading, setRisposteTipiLoading] = useState(false);
  const [risposteTipiDialogOpen, setRisposteTipiDialogOpen] = useState(false);
  const [risposteTipiSaving, setRisposteTipiSaving] = useState(false);
  const [risposteTipiSearch, setRisposteTipiSearch] = useState('');
  const [risposteTipiOnlyActive, setRisposteTipiOnlyActive] = useState(true);
  const [risposteTipiNewOpen, setRisposteTipiNewOpen] = useState(false);
  const [richiesteDocSettings, setRichiesteDocSettings] = useState(null);
  const [richiesteDocSettingsLoading, setRichiesteDocSettingsLoading] = useState(false);
  const [richiesteDocSettingsSaving, setRichiesteDocSettingsSaving] = useState(false);
  const [richiesteDocSettingsAllowIncomplete, setRichiesteDocSettingsAllowIncomplete] = useState(false);
  const [richiesteDocSettingsDefaultIssuer, setRichiesteDocSettingsDefaultIssuer] = useState(null);
  const [richiesteDocSettingsIssuerQuery, setRichiesteDocSettingsIssuerQuery] = useState('');
  const [richiesteDocSettingsIssuerOptions, setRichiesteDocSettingsIssuerOptions] = useState([]);
  const [richiesteDocSettingsIssuerLoading, setRichiesteDocSettingsIssuerLoading] = useState(false);
  const [richiesteDocSettingsLegalUploading, setRichiesteDocSettingsLegalUploading] = useState(false);
  const legalDocFileRef = useRef(null);
  const [newTipo, setNewTipo] = useState({
    code: '',
    label: '',
    required: false,
    active: true,
    sort_order: 0
  });
  const [rispostaUploadCtx, setRispostaUploadCtx] = useState(null);
  const [rispostaUploading, setRispostaUploading] = useState(false);
  const [rispostaDeleting, setRispostaDeleting] = useState(false);
  const rispostaFileRef = useRef(null);
  const [rispostaUpdating, setRispostaUpdating] = useState(false);

  const [richiestaDocDialogOpen, setRichiestaDocDialogOpen] = useState(false);
  const [richiestaDocTarget, setRichiestaDocTarget] = useState(null);
  const [richiestaDocFiles, setRichiestaDocFiles] = useState({
    contratto: null,
    mandato: null,
    doc_identita: null,
    visura: null,
    questionario: null
  });
  const [richiestaDocExisting, setRichiestaDocExisting] = useState(null);
  const [richiestaDocExistingLoading, setRichiestaDocExistingLoading] = useState(false);
  const [richiestaDocDuplicates, setRichiestaDocDuplicates] = useState({});
  const [richiestaDocUnmatched, setRichiestaDocUnmatched] = useState([]);
  const [richiestaDocSaving, setRichiestaDocSaving] = useState(false);
  const [richiestaDocDeleting, setRichiestaDocDeleting] = useState(false);
  const [richiestaDocStatusSaving, setRichiestaDocStatusSaving] = useState(false);
  const [richiestaDocPecSending, setRichiestaDocPecSending] = useState(false);
  const [pecAccounts, setPecAccounts] = useState([]);
  const [pecAccountsLoading, setPecAccountsLoading] = useState(false);
  const [selectedPecAccountId, setSelectedPecAccountId] = useState('');
  const [issuerItemPecSendingId, setIssuerItemPecSendingId] = useState(null);
  const [issuerItemPecDialogOpen, setIssuerItemPecDialogOpen] = useState(false);
  const [issuerItemPecSending, setIssuerItemPecSending] = useState(false);
  const [issuerItemPecKind, setIssuerItemPecKind] = useState('pec');
  const [issuerItemPecCtx, setIssuerItemPecCtx] = useState(null);
  const [issuerItemPecAttachmentTypes, setIssuerItemPecAttachmentTypes] = useState([]);
  const [modelli, setModelli] = useState([]);
  const [modelliLoading, setModelliLoading] = useState(false);
  const [issuerItemPecTab, setIssuerItemPecTab] = useState(0);
  const [issuerItemPecForm, setIssuerItemPecForm] = useState({
    accountId: '',
    modelloId: '',
    modelloName: '',
    modelloContent: '',
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: ''
  });
  const [richiestaDocDeleteConfirmOpen, setRichiestaDocDeleteConfirmOpen] = useState(false);
  const richiestaDocFileRef = useRef(null);

  const richiestaDocRagioneSociale = String(richiestaDocTarget?.dossier?.customer_ragione_sociale || '').trim();

  const richiestaDocIsAzienda = useMemo(() => Boolean(richiestaDocRagioneSociale), [richiestaDocRagioneSociale]);

  const richiestaDocRequiredTypes = useMemo(
    () => (richiestaDocIsAzienda ? ['contratto', 'mandato', 'doc_identita', 'visura'] : ['contratto', 'mandato', 'doc_identita']),
    [richiestaDocIsAzienda]
  );

  const richiestaDocOptionalTypes = useMemo(() => ['questionario'], []);

  const richiestaDocAllTypes = useMemo(() => {
    const required = Array.isArray(richiestaDocRequiredTypes) ? richiestaDocRequiredTypes : [];
    const optional = Array.isArray(richiestaDocOptionalTypes) ? richiestaDocOptionalTypes : [];
    return [...required, ...optional.filter((t) => !required.includes(t))];
  }, [richiestaDocOptionalTypes, richiestaDocRequiredTypes]);

  const richiestaDocRequiredTotal = richiestaDocRequiredTypes.length;

  const richiestaDocPresentCount = useMemo(() => {
    const existing = richiestaDocExisting || {};
    return richiestaDocRequiredTypes.filter((type) => {
      if (type === 'contratto') return Boolean(richiestaDocFiles?.contratto || existing?.contratto_file_path);
      if (type === 'mandato') return Boolean(richiestaDocFiles?.mandato || existing?.mandato_file_path);
      if (type === 'doc_identita') return Boolean(richiestaDocFiles?.doc_identita || existing?.doc_identita_file_path);
      if (type === 'visura') return Boolean(richiestaDocFiles?.visura || existing?.visura_file_path);
      return false;
    }).length;
  }, [richiestaDocFiles, richiestaDocExisting, richiestaDocRequiredTypes]);

  const richiestaDocIsPartial =
    richiestaDocPresentCount > 0 && richiestaDocPresentCount < richiestaDocRequiredTotal;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [openInNewTabLoading, setOpenInNewTabLoading] = useState(false);
  const [richiestaDocPreviewOpen, setRichiestaDocPreviewOpen] = useState(false);
  const [richiestaDocPreviewUrl, setRichiestaDocPreviewUrl] = useState('');
  const [richiestaDocPreviewLoading, setRichiestaDocPreviewLoading] = useState(false);
  const [richiestaDocPreviewRow, setRichiestaDocPreviewRow] = useState(null);
  const [richiestaDocPreviewOpenInNewTabLoading, setRichiestaDocPreviewOpenInNewTabLoading] = useState(false);

  const fetchRows = async (
    pageArg = page,
    limitArg = rowsPerPage,
    searchArg = search,
    statoArg = stato,
    orderByArg = orderBy,
    orderArg = order,
    diffidaArg = diffidaFilter
  ) => {
    setLoading(true);
    try {
      const res = await api.get('/richieste-doc', {
        params: {
          page: pageArg + 1,
          limit: limitArg,
          search: searchArg || '',
          stato: statoArg || 'all',
          diffida: diffidaArg || 'all',
          orderBy: orderByArg || 'created_at',
          order: orderArg || 'desc'
        }
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento richieste documenti');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (searchArg = search) => {
    setStatsLoading(true);
    try {
      const res = await api.get('/richieste-doc/stats', { params: { search: searchArg || '' } });
      const data = res.data?.data || {};
      setStats({
        predisposta: Number(data?.predisposta || 0),
        pec_inviata_parziale: Number(data?.pec_inviata_parziale || 0),
        inviata: Number(data?.inviata || 0),
        pec_senza_risposta: Number(data?.pec_senza_risposta || 0),
        non_gestire: Number(data?.non_gestire || 0),
        diffida_inviata: Number(data?.diffida_inviata || 0),
        diffida_da_inviare: Number(data?.diffida_da_inviare || 0)
      });
    } catch (_error) {
      setStats({
        predisposta: 0,
        pec_inviata_parziale: 0,
        inviata: 0,
        pec_senza_risposta: 0,
        non_gestire: 0,
        diffida_inviata: 0,
        diffida_da_inviare: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(0, rowsPerPage, search, stato);
    fetchStats(search);
  }, []);

  const fetchRisposteTipi = async () => {
    setRisposteTipiLoading(true);
    try {
      const res = await api.get('/richieste-doc/risposte/tipi');
      setRisposteTipi(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      setRisposteTipi([]);
    } finally {
      setRisposteTipiLoading(false);
    }
  };

  const fetchRichiesteDocSettings = async () => {
    setRichiesteDocSettingsLoading(true);
    try {
      const res = await api.get('/richieste-doc/settings');
      const settings = res.data?.settings || null;
      setRichiesteDocSettings(settings);
      setRichiesteDocSettingsAllowIncomplete(Boolean(settings?.allow_incomplete_generation));

      const defaultIssuerId = settings?.default_issuer_id != null ? Number(settings.default_issuer_id) : null;
      if (Number.isInteger(defaultIssuerId) && defaultIssuerId > 0) {
        try {
          const issuerRes = await api.get('/richieste-doc/issuers/search', { params: { q: String(defaultIssuerId) } });
          const opt = (issuerRes.data?.data || []).find((x) => Number(x?.id) === defaultIssuerId) || null;
          setRichiesteDocSettingsDefaultIssuer(opt);
        } catch (_e) {
          setRichiesteDocSettingsDefaultIssuer({ id: defaultIssuerId, name: `Issuer #${defaultIssuerId}` });
        }
      } else {
        setRichiesteDocSettingsDefaultIssuer(null);
      }
    } catch (_error) {
      setRichiesteDocSettings(null);
      setRichiesteDocSettingsAllowIncomplete(false);
      setRichiesteDocSettingsDefaultIssuer(null);
    } finally {
      setRichiesteDocSettingsLoading(false);
    }
  };

  useEffect(() => {
    fetchRisposteTipi();
    fetchRichiesteDocSettings();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchRows(0, rowsPerPage, search, stato);
      fetchStats(search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search, stato, diffidaFilter, rowsPerPage]);

  useEffect(() => {
    if (!risposteTipiDialogOpen) return;
    const timer = setTimeout(async () => {
      setRichiesteDocSettingsIssuerLoading(true);
      try {
        const res = await api.get('/richieste-doc/issuers/search', {
          params: { q: String(richiesteDocSettingsIssuerQuery || '').trim() }
        });
        setRichiesteDocSettingsIssuerOptions(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (_e) {
        setRichiesteDocSettingsIssuerOptions([]);
      } finally {
        setRichiesteDocSettingsIssuerLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [risposteTipiDialogOpen, richiesteDocSettingsIssuerQuery]);

  const statoWidgets = useMemo(
    () => [
      { key: 'predisposta', title: 'Predisposte', value: stats.predisposta, Icon: AlertTriangle, color: '#f59e0b' },
      { key: 'pec_inviata_parziale', title: 'PEC inviata (parziale)', value: stats.pec_inviata_parziale, Icon: Mail, color: '#f97316' },
      { key: 'inviata', title: 'Inviate', value: stats.inviata, Icon: Send, color: '#2563eb' },
      { key: 'pec_senza_risposta', title: 'PEC inviata senza risposta', value: stats.pec_senza_risposta, Icon: Mail, color: '#ef4444' },
      { key: 'non_gestire', title: 'Non gestire', value: stats.non_gestire, Icon: XCircle, color: '#64748b' }
    ],
    [stats]
  );

  const diffidaWidgets = useMemo(
    () => [
      { key: 'inviata', title: 'Diffide inviate', value: stats.diffida_inviata, Icon: Send, color: '#ef4444' },
      { key: 'da_inviare', title: 'Diffide da inviare', value: stats.diffida_da_inviare, Icon: AlertTriangle, color: '#f59e0b' }
    ],
    [stats]
  );

  const handleChangePage = (_event, nextPage) => {
    setPage(nextPage);
    fetchRows(nextPage, rowsPerPage, search, stato);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10) || 25;
    setRowsPerPage(next);
    setPage(0);
    fetchRows(0, next, search, stato);
  };

  const handleRequestSortDataConferimento = () => {
    const isSame = orderBy === 'data_conferimento';
    const nextOrder = isSame && order === 'desc' ? 'asc' : 'desc';
    const nextOrderBy = 'data_conferimento';
    setOrderBy(nextOrderBy);
    setOrder(nextOrder);
    setPage(0);
    fetchRows(0, rowsPerPage, search, stato, nextOrderBy, nextOrder);
  };

  const handleRequestSortCreatedAt = () => {
    const isSame = orderBy === 'created_at';
    const nextOrder = isSame && order === 'desc' ? 'asc' : 'desc';
    const nextOrderBy = 'created_at';
    setOrderBy(nextOrderBy);
    setOrder(nextOrder);
    setPage(0);
    fetchRows(0, rowsPerPage, search, stato, nextOrderBy, nextOrder);
  };

  const loadIssuers = async (richiestaId) => {
    if (issuerLoading[richiestaId]) return;
    setIssuerLoading((prev) => ({ ...prev, [richiestaId]: true }));
    try {
      const res = await api.get(`/richieste-doc/${richiestaId}/issuers`);
      setIssuerRows((prev) => ({ ...prev, [richiestaId]: Array.isArray(res.data?.data) ? res.data.data : [] }));
    } catch (error) {
      setIssuerRows((prev) => ({ ...prev, [richiestaId]: [] }));
    } finally {
      setIssuerLoading((prev) => ({ ...prev, [richiestaId]: false }));
    }
  };

  const closeIssuerItemMenu = () => {
    setIssuerItemMenuAnchorEl(null);
    setIssuerItemMenuCtx(null);
  };

  const openIssuerItemMenu = (event, ctx) => {
    event.stopPropagation();
    setIssuerItemMenuAnchorEl(event.currentTarget);
    setIssuerItemMenuCtx(ctx || null);
  };

  const getIssuerItemRestoreStato = (item) => {
    if (!item) return 'predisposta';
    if (item?.errore_codice || item?.errore_messaggio) return 'errore';
    if (item?.ricevuta_il) return 'ricevuta';
    if (item?.inviata_il) return 'inviata';
    return 'predisposta';
  };

  const updateIssuerItemStato = async (ctx, nextStato) => {
    if (issuerItemStatoSaving || issuerItemPecSending || richiestaDocPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving) {
      return;
    }
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;
    const statoItem = String(nextStato || '').trim().toLowerCase();
    if (!['predisposta', 'inviata', 'inviata_manualmente', 'ricevuta', 'sollecito', 'errore', 'non_gestire'].includes(statoItem)) return;
    try {
      setIssuerItemStatoSaving(true);
      setIssuerItemStatoSavingId(iid);
      await api.patch(`/richieste-doc/issuer-item/${iid}/stato`, { stato_item: statoItem });
      setIssuerRows((prev) => {
        const list = Array.isArray(prev?.[rid]) ? prev[rid] : [];
        return {
          ...prev,
          [rid]: list.map((x) => (Number(x?.id) === iid ? { ...x, stato_item: statoItem } : x))
        };
      });
      toast.success('Stato creditore aggiornato');
      closeIssuerItemMenu();
      await loadIssuers(rid);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento stato creditore');
    } finally {
      setIssuerItemStatoSaving(false);
      setIssuerItemStatoSavingId(null);
    }
  };

  const openAddIssuerDrawer = (ctx) => {
    const rid = Number(ctx?.richiestaId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    setAddIssuerCtx({ richiestaId: rid });
    setAddIssuerQuery('');
    setAddIssuerOptions([]);
    setAddIssuerSelected(null);
    setAddIssuerError('');
    setAddIssuerDrawerOpen(true);
  };

  const closeAddIssuerDrawer = () => {
    if (addIssuerSaving) return;
    setAddIssuerDrawerOpen(false);
    setAddIssuerCtx(null);
    setAddIssuerQuery('');
    setAddIssuerOptions([]);
    setAddIssuerSelected(null);
    setAddIssuerLoading(false);
    setAddIssuerError('');
  };

  useEffect(() => {
    if (!addIssuerDrawerOpen) return;
    const q = String(addIssuerQuery || '').trim();
    if (q.length === 1) {
      setAddIssuerOptions([]);
      setAddIssuerLoading(false);
      setAddIssuerError('');
      return;
    }
    let cancelled = false;
    setAddIssuerLoading(true);
    setAddIssuerError('');
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/richieste-doc/issuers/search', { params: { q } });
        if (cancelled) return;
        setAddIssuerOptions(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (_error) {
        if (cancelled) return;
        setAddIssuerOptions([]);
        setAddIssuerError('Errore caricamento creditori. Verifica la connessione al remote DB.');
      } finally {
        if (cancelled) return;
        setAddIssuerLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addIssuerDrawerOpen, addIssuerQuery]);

  const submitAddIssuer = async () => {
    if (addIssuerSaving) return;
    const rid = Number(addIssuerCtx?.richiestaId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    const issuerId = Number(addIssuerSelected?.id);
    if (!Number.isInteger(issuerId) || issuerId <= 0) {
      toast.error('Seleziona un creditore');
      return;
    }
    try {
      setAddIssuerSaving(true);
      await api.post(`/richieste-doc/${rid}/issuers`, { issuer_id: issuerId });
      toast.success('Creditore aggiunto');
      await loadIssuers(rid);
      fetchRows(page, rowsPerPage, search, stato);
      closeAddIssuerDrawer();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiunta creditore');
    } finally {
      setAddIssuerSaving(false);
    }
  };

  const toDatetimeLocalValue = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startIssuerItemInvioEdit = (ctx) => {
    if (issuerItemInvioSavingId != null) return;
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;
    const item = ctx?.item || null;
    const original = toDatetimeLocalValue(item?.inviata_il);
    setIssuerItemInvioEditId(iid);
    setIssuerItemInvioEditOriginal(original);
    setIssuerItemInvioEditValue(original);
  };

  const cancelIssuerItemInvioEdit = () => {
    if (issuerItemInvioSavingId != null) return;
    setIssuerItemInvioEditId(null);
    setIssuerItemInvioEditValue('');
    setIssuerItemInvioEditOriginal('');
  };

  const saveIssuerItemInvioEdit = async (ctx) => {
    if (issuerItemInvioSavingId != null || issuerItemPecSending || richiestaDocPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving) {
      return;
    }
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;

    const payloadValue = String(issuerItemInvioEditValue || '').trim();
    if (payloadValue === String(issuerItemInvioEditOriginal || '').trim()) {
      cancelIssuerItemInvioEdit();
      return;
    }

    try {
      setIssuerItemInvioSavingId(iid);
      const res = await api.patch(`/richieste-doc/issuer-item/${iid}/inviata-il`, {
        inviata_il: payloadValue ? payloadValue : null
      });
      const updated = res?.data?.item || null;
      setIssuerRows((prev) => {
        const list = Array.isArray(prev?.[rid]) ? prev[rid] : [];
        return {
          ...prev,
          [rid]: list.map((x) => (Number(x?.id) === iid ? { ...x, ...updated } : x))
        };
      });
      toast.success('Data invio aggiornata');
      cancelIssuerItemInvioEdit();
      await loadIssuers(rid);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento data invio');
    } finally {
      setIssuerItemInvioSavingId(null);
    }
  };

  const startIssuerItemDiffidaEdit = (ctx) => {
    if (issuerItemDiffidaSavingId != null) return;
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;
    const item = ctx?.item || null;
    const original = toDatetimeLocalValue(item?.diffida_inviata_il);
    setIssuerItemDiffidaEditId(iid);
    setIssuerItemDiffidaEditOriginal(original);
    setIssuerItemDiffidaEditValue(original);
  };

  const cancelIssuerItemDiffidaEdit = () => {
    if (issuerItemDiffidaSavingId != null) return;
    setIssuerItemDiffidaEditId(null);
    setIssuerItemDiffidaEditValue('');
    setIssuerItemDiffidaEditOriginal('');
  };

  const saveIssuerItemDiffidaEdit = async (ctx) => {
    if (issuerItemDiffidaSavingId != null || issuerItemPecSending || richiestaDocPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving) {
      return;
    }
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;

    const payloadValue = String(issuerItemDiffidaEditValue || '').trim();
    if (payloadValue === String(issuerItemDiffidaEditOriginal || '').trim()) {
      cancelIssuerItemDiffidaEdit();
      return;
    }

    try {
      setIssuerItemDiffidaSavingId(iid);
      const res = await api.patch(`/richieste-doc/issuer-item/${iid}/diffida-il`, {
        diffida_inviata_il: payloadValue ? payloadValue : null
      });
      const updated = res?.data?.item || null;
      setIssuerRows((prev) => {
        const list = Array.isArray(prev?.[rid]) ? prev[rid] : [];
        return {
          ...prev,
          [rid]: list.map((x) => (Number(x?.id) === iid ? { ...x, ...updated } : x))
        };
      });
      toast.success('Data diffida aggiornata');
      cancelIssuerItemDiffidaEdit();
      await loadIssuers(rid);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento data diffida');
    } finally {
      setIssuerItemDiffidaSavingId(null);
    }
  };

  const toggleExpanded = (richiestaId) => {
    setExpanded((prev) => {
      if (prev[richiestaId]) return {};
      return { [richiestaId]: true };
    });
    setIssuerExpanded({});
    if (!issuerRows[richiestaId] && !issuerLoading[richiestaId]) {
      loadIssuers(richiestaId);
    }
  };

  const resetRichiestaDocDialog = () => {
    setRichiestaDocFiles({
      contratto: null,
      mandato: null,
      doc_identita: null,
      visura: null,
      questionario: null
    });
    setRichiestaDocExisting(null);
    setRichiestaDocExistingLoading(false);
    setRichiestaDocDuplicates({});
    setRichiestaDocUnmatched([]);
    setRichiestaDocDeleting(false);
    setRichiestaDocStatusSaving(false);
    setRichiestaDocPecSending(false);
    setRichiestaDocDeleteConfirmOpen(false);
    if (richiestaDocPreviewUrl) {
      window.URL.revokeObjectURL(richiestaDocPreviewUrl);
      setRichiestaDocPreviewUrl('');
    }
    setRichiestaDocPreviewOpen(false);
    setRichiestaDocPreviewLoading(false);
    setRichiestaDocPreviewRow(null);
    if (richiestaDocFileRef.current) richiestaDocFileRef.current.value = '';
  };

  const fetchPecAccounts = async () => {
    setPecAccountsLoading(true);
    try {
      const res = await api.get('/richieste-doc/pec-accounts');
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setPecAccounts(list);
      setSelectedPecAccountId((prev) => {
        if (String(prev || '').trim()) return prev;
        const first = list?.[0]?.id != null ? String(list[0].id) : '';
        return first;
      });
      return list;
    } catch (error) {
      setPecAccounts([]);
      setSelectedPecAccountId('');
      return [];
    } finally {
      setPecAccountsLoading(false);
    }
  };

  const fetchModelli = async () => {
    setModelliLoading(true);
    try {
      const res = await api.get('/modulistica');
      setModelli(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Errore caricamento modelli:', error);
      setModelli([]);
    } finally {
      setModelliLoading(false);
    }
  };

  const refreshRichiestaDocExisting = async (dossierId) => {
    if (!Number.isInteger(dossierId) || dossierId <= 0) return;
    setRichiestaDocExistingLoading(true);
    try {
      const res = await api.get(`/dossier/${dossierId}/richieste-doc`);
      setRichiestaDocExisting(res?.data?.richiesta || null);
    } catch (error) {
      setRichiestaDocExisting(null);
    } finally {
      setRichiestaDocExistingLoading(false);
    }
  };

  const openRichiestaDocDialog = async (row) => {
    const dossierId = Number(row?.dossier_id);
    setRichiestaDocTarget(row || null);
    resetRichiestaDocDialog();
    setRichiestaDocDialogOpen(true);
    if (!pecAccountsLoading && (pecAccounts || []).length === 0) {
      fetchPecAccounts();
    }
    await refreshRichiestaDocExisting(dossierId);
  };

  const closeRichiestaDocDialog = () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    setRichiestaDocDialogOpen(false);
    setRichiestaDocTarget(null);
    resetRichiestaDocDialog();
  };

  const handlePickRichiestaDocFiles = () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    richiestaDocFileRef.current?.click?.();
  };

  const handleRichiestaDocFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;

    const nextFiles = { ...(richiestaDocFiles || {}) };
    const nextDup = { ...(richiestaDocDuplicates || {}) };
    const nextUnmatched = [...(richiestaDocUnmatched || [])];

    picked.forEach((file) => {
      const type = detectRichiestaDocTypeFromName(file?.name);
      if (!type) {
        nextUnmatched.push(file);
        return;
      }
      if (type === 'visura' && !richiestaDocIsAzienda) {
        nextUnmatched.push(file);
        return;
      }
      if (!nextFiles[type]) {
        nextFiles[type] = file;
        return;
      }
      nextDup[type] = [...(nextDup[type] || []), file];
    });

    setRichiestaDocFiles(nextFiles);
    setRichiestaDocDuplicates(nextDup);
    setRichiestaDocUnmatched(nextUnmatched);
  };

  const handlePickRispostaFiles = (richiestaId, issuerItemId) => {
    if (rispostaUploading || rispostaDeleting || rispostaUpdating) return;
    if (!Number.isInteger(richiestaId) || richiestaId <= 0) return;
    if (!Number.isInteger(issuerItemId) || issuerItemId <= 0) return;
    setRispostaUploadCtx({ richiestaId, issuerItemId });
    rispostaFileRef.current?.click?.();
  };

  const handleRispostaFileSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    const ctx = rispostaUploadCtx;
    setRispostaUploadCtx(null);
    if (!ctx || files.length === 0) return;
    try {
      setRispostaUploading(true);
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      await api.post(`/richieste-doc/issuer-item/${ctx.issuerItemId}/risposte-files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Allegati caricati');
      await loadIssuers(ctx.richiestaId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore upload allegati');
    } finally {
      setRispostaUploading(false);
    }
  };

  const handleUpdateRispostaFileTipo = async (richiestaId, fileId, tipoIdRaw) => {
    if (rispostaUploading || rispostaDeleting || rispostaUpdating) return;
    if (!Number.isInteger(richiestaId) || richiestaId <= 0) return;
    if (!Number.isInteger(fileId) || fileId <= 0) return;
    const tipoId = String(tipoIdRaw || '').trim() ? Number(tipoIdRaw) : null;
    if (tipoId != null && (!Number.isInteger(tipoId) || tipoId <= 0)) return;
    try {
      setRispostaUpdating(true);
      await api.patch(`/richieste-doc/risposte-files/${fileId}`, {
        tipo_id: tipoId == null ? null : tipoId
      });
      await loadIssuers(richiestaId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento tipo');
    } finally {
      setRispostaUpdating(false);
    }
  };

  const openRisposteTipiDialog = () => {
    setRisposteTipiDialogOpen(true);
    setRisposteTipiSearch('');
    setRisposteTipiOnlyActive(true);
    setRisposteTipiNewOpen(false);
    setRichiesteDocSettingsIssuerQuery('');
    setRichiesteDocSettingsIssuerOptions([]);
    fetchRichiesteDocSettings();
  };

  const closeRisposteTipiDialog = () => {
    if (risposteTipiSaving) return;
    setRisposteTipiDialogOpen(false);
    setNewTipo({ code: '', label: '', required: false, active: true, sort_order: 0 });
    setRisposteTipiSearch('');
    setRisposteTipiOnlyActive(true);
    setRisposteTipiNewOpen(false);
    setRichiesteDocSettingsIssuerQuery('');
    setRichiesteDocSettingsIssuerOptions([]);
  };

  const saveRichiesteDocSettings = async () => {
    if (richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading) return;
    try {
      setRichiesteDocSettingsSaving(true);
      await api.put('/richieste-doc/settings', {
        allow_incomplete_generation: Boolean(richiesteDocSettingsAllowIncomplete),
        default_issuer_id: richiesteDocSettingsDefaultIssuer?.id != null ? Number(richiesteDocSettingsDefaultIssuer.id) : null
      });
      toast.success('Impostazioni salvate');
      await fetchRichiesteDocSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio impostazioni');
    } finally {
      setRichiesteDocSettingsSaving(false);
    }
  };

  const pickLegalDocFile = () => {
    if (richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading) return;
    legalDocFileRef.current?.click?.();
  };

  const uploadLegalDocFile = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;
    try {
      setRichiesteDocSettingsLegalUploading(true);
      const form = new FormData();
      form.append('file', file);
      await api.post('/richieste-doc/settings/legal-doc', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento legale aggiornato');
      await fetchRichiesteDocSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore upload documento legale');
    } finally {
      setRichiesteDocSettingsLegalUploading(false);
    }
  };

  const clearLegalDocFile = async () => {
    if (richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading) return;
    try {
      setRichiesteDocSettingsLegalUploading(true);
      await api.delete('/richieste-doc/settings/legal-doc');
      toast.success('Documento legale rimosso');
      await fetchRichiesteDocSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore rimozione documento legale');
    } finally {
      setRichiesteDocSettingsLegalUploading(false);
    }
  };

  const downloadLegalDocFile = async () => {
    try {
      const res = await api.get('/richieste-doc/settings/legal-doc/download', { responseType: 'blob' });
      const name = richiesteDocSettings?.legal_doc_file_name || 'doc_identita_legale';
      const blob = new Blob([res.data], { type: richiesteDocSettings?.legal_doc_mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore download documento legale');
    }
  };

  const handleCreateTipo = async () => {
    if (risposteTipiSaving) return;
    const code = String(newTipo?.code || '').trim();
    const label = String(newTipo?.label || '').trim();
    if (!code) {
      toast.error('Inserisci il code');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(code)) {
      toast.error('Code non valido (usa solo a-z, 0-9, underscore)');
      return;
    }
    if (!label) {
      toast.error('Inserisci la label');
      return;
    }
    try {
      setRisposteTipiSaving(true);
      await api.post('/richieste-doc/risposte/tipi', {
        code,
        label,
        required: Boolean(newTipo?.required),
        active: Boolean(newTipo?.active),
        sort_order: Number(newTipo?.sort_order || 0)
      });
      toast.success('Tipo creato');
      setNewTipo({ code: '', label: '', required: false, active: true, sort_order: 0 });
      await fetchRisposteTipi();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore creazione tipo');
    } finally {
      setRisposteTipiSaving(false);
    }
  };

  const handleUpdateTipo = async (tipo, patch) => {
    if (risposteTipiSaving) return;
    const id = Number(tipo?.id);
    if (!Number.isInteger(id) || id <= 0) return;
    try {
      setRisposteTipiSaving(true);
      await api.put(`/richieste-doc/risposte/tipi/${id}`, patch);
      toast.success('Tipo aggiornato');
      await fetchRisposteTipi();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento tipo');
    } finally {
      setRisposteTipiSaving(false);
    }
  };

  const filteredRisposteTipi = useMemo(() => {
    const term = String(risposteTipiSearch || '').trim().toLowerCase();
    const onlyActive = Boolean(risposteTipiOnlyActive);
    return (risposteTipi || []).filter((t) => {
      if (onlyActive && !Boolean(t?.active)) return false;
      if (!term) return true;
      const code = String(t?.code || '').toLowerCase();
      const label = String(t?.label || '').toLowerCase();
      return code.includes(term) || label.includes(term);
    });
  }, [risposteTipi, risposteTipiOnlyActive, risposteTipiSearch]);

  const handleDeleteRispostaFile = async (richiestaId, fileId) => {
    if (rispostaUploading || rispostaDeleting) return;
    if (!Number.isInteger(richiestaId) || richiestaId <= 0) return;
    if (!Number.isInteger(fileId) || fileId <= 0) return;
    try {
      setRispostaDeleting(true);
      await api.delete(`/richieste-doc/risposte-files/${fileId}`);
      toast.success('Documento eliminato');
      await loadIssuers(richiestaId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione documento');
    } finally {
      setRispostaDeleting(false);
    }
  };

  const handleUpdateRichiestaStato = async (nextStato) => {
    if (richiestaDocStatusSaving || richiestaDocSaving || richiestaDocDeleting) return;
    const richiestaId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(richiestaId) || richiestaId <= 0) {
      toast.error('Richiesta non valida');
      return;
    }
    try {
      setRichiestaDocStatusSaving(true);
      const res = await api.patch(`/richieste-doc/${richiestaId}/stato`, { stato: nextStato });
      const updated = res?.data?.richiesta || null;
      setRichiestaDocTarget((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          stato_richiesta: nextStato,
          inviata_il: updated?.inviata_il != null ? updated.inviata_il : prev.inviata_il
        };
      });
      toast.success('Stato aggiornato');
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore aggiornamento stato');
    } finally {
      setRichiestaDocStatusSaving(false);
    }
  };

  const sendRichiestaPec = async () => {
    if (richiestaDocPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving) return;
    const richiestaId = Number(richiestaDocTarget?.id);
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(richiestaId) || richiestaId <= 0) {
      toast.error('Richiesta non valida');
      return;
    }
    try {
      setRichiestaDocPecSending(true);
      const payload = {};
      const accountId = Number(selectedPecAccountId);
      if (Number.isInteger(accountId) && accountId > 0) {
        payload.accountId = accountId;
      }
      const res = await api.post(`/richieste-doc/${richiestaId}/invia-pec`, payload);
      const sent = Number(res.data?.sent || 0);
      const failed = Number(res.data?.failed || 0);
      toast.success(`PEC inviate: ${sent}${failed ? ` · Errori: ${failed}` : ''}`);
      await refreshRichiestaDocExisting(dossierId);
      fetchRows(page, rowsPerPage, search, stato);
      await loadIssuers(richiestaId);
      setRichiestaDocTarget((prev) => {
        if (!prev) return prev;
        return { ...prev, stato_richiesta: 'inviata' };
      });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore invio PEC');
    } finally {
      setRichiestaDocPecSending(false);
    }
  };

  const openIssuerItemPecDialog = async (ctx) => {
    if (issuerItemPecSending || richiestaDocPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving) return;
    const rid = Number(ctx?.richiestaId);
    const iid = Number(ctx?.issuerItemId);
    if (!Number.isInteger(rid) || rid <= 0) return;
    if (!Number.isInteger(iid) || iid <= 0) return;
    
    if (modelli.length === 0 && !modelliLoading) {
      fetchModelli();
    }
    
    const list = (pecAccounts || []).length ? pecAccounts : (!pecAccountsLoading ? await fetchPecAccounts() : []);
    const accountId = Number(selectedPecAccountId) || Number(list?.[0]?.id);
    const kind = String(ctx?.kind || 'pec').trim().toLowerCase() === 'diffida' ? 'diffida' : 'pec';
    setIssuerItemPecKind(kind);
    setIssuerItemPecCtx({
      richiestaId: rid,
      issuerItemId: iid,
      issuerName: ctx?.issuerName || '',
      issuerPec: ctx?.issuerPec || '',
      dossierId: ctx?.dossierId || null,
      customerLabel: ctx?.customerLabel || '',
      customerEmail: ctx?.customerEmail || '',
      attachments: Array.isArray(ctx?.attachments) ? ctx.attachments : []
    });
    setIssuerItemPecAttachmentTypes(
      Array.from(
        new Set(
          (Array.isArray(ctx?.attachments) ? ctx.attachments : [])
            .map((x) => String(x?.type || '').trim().toLowerCase())
            .filter(Boolean)
        )
      )
    );
    const customer = String(ctx?.customerLabel || '').trim();
    const issuerName = String(ctx?.issuerName || '').trim();
    const dossierId = Number(ctx?.dossierId);
    const subjectParts = [customer, issuerName].filter(Boolean);
    const subject =
      kind === 'diffida'
        ? `${subjectParts.join(' / ')}`.trim()
        : `Richiesta documentazione${subjectParts.length ? ` - ${subjectParts.join(' / ')}` : ''}`.trim();
    const body =
      kind === 'diffida'
        ? `Spett.le Creditore,\n\ncon la presente Vi diffidiamo a trasmettere entro e non oltre 7 giorni dal ricevimento della presente la documentazione richiesta relativa al dossier #${
            Number.isInteger(dossierId) ? dossierId : ''
          }.\n\nIn difetto, ci vedremo costretti a tutelare gli interessi del cliente nelle sedi opportune.\n\nCordiali saluti`
        : `Spett.le Creditore,\n\nin allegato trasmettiamo la documentazione relativa al dossier #${Number.isInteger(dossierId) ? dossierId : ''}.\n\nCordiali saluti`;
    setIssuerItemPecForm({
      accountId: Number.isInteger(accountId) && accountId > 0 ? String(accountId) : '',
      modelloId: '',
      to: String(ctx?.issuerPec || '').trim(),
      cc: '',
      bcc: String(ctx?.customerEmail || '').trim(),
      subject,
      body
    });
    setIssuerItemPecDialogOpen(true);
  };

  const closeIssuerItemPecDialog = () => {
    if (issuerItemPecSending) return;
    setIssuerItemPecDialogOpen(false);
    setIssuerItemPecKind('pec');
    setIssuerItemPecCtx(null);
    setIssuerItemPecTab(0);
    setIssuerItemPecForm({ accountId: '', modelloId: '', modelloName: '', modelloContent: '', to: '', cc: '', bcc: '', subject: '', body: '' });
    setIssuerItemPecAttachmentTypes([]);
  };

  const submitIssuerItemPecDialog = async () => {
    if (issuerItemPecSending) return;
    const ctx = issuerItemPecCtx;
    const iid = Number(ctx?.issuerItemId);
    const rid = Number(ctx?.richiestaId);
    if (!Number.isInteger(iid) || iid <= 0) return;
    if (!Number.isInteger(rid) || rid <= 0) return;
    const accountId = Number(issuerItemPecForm?.accountId);
    if (!Number.isInteger(accountId) || accountId <= 0) {
      toast.error('Seleziona una PEC');
      return;
    }
    const to = String(issuerItemPecForm?.to || '').trim();
    if (!to) {
      toast.error('Inserisci il destinatario');
      return;
    }
    const selectedTypes = Array.isArray(issuerItemPecAttachmentTypes) ? issuerItemPecAttachmentTypes : [];
    if (!richiesteDocSettings?.legal_doc_file_path) {
      toast.error('Documento identità legale non configurato: caricalo nei Parametri prima di inviare PEC');
      return;
    }
    const modelloContent = String(issuerItemPecForm?.modelloContent || '').trim();
    if (issuerItemPecKind === 'diffida' && !String(issuerItemPecForm?.modelloId || '').trim()) {
      toast.error('Seleziona un modello di diffida');
      return;
    }
    const hasAnyAttachmentChoice = selectedTypes.length > 0 || Boolean(modelloContent);
    if (!hasAnyAttachmentChoice) {
      toast.error('Seleziona almeno un allegato (o un modello)');
      return;
    }
    try {
      setIssuerItemPecSending(true);
      setIssuerItemPecSendingId(iid);
      await api.post(`/richieste-doc/issuer-item/${iid}/invia-pec`, {
        kind: issuerItemPecKind,
        accountId,
        to,
        cc: String(issuerItemPecForm?.cc || '').trim(),
        bcc: String(issuerItemPecForm?.bcc || '').trim(),
        subject: String(issuerItemPecForm?.subject || '').trim(),
        body: String(issuerItemPecForm?.body || '').trim(),
        modelloName: String(issuerItemPecForm?.modelloName || '').trim(),
        modelloContent,
        attachmentTypes: selectedTypes
      });
      toast.success(issuerItemPecKind === 'diffida' ? 'Diffida inviata al creditore' : 'PEC inviata al creditore');
      closeIssuerItemPecDialog();
      await loadIssuers(rid);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore invio PEC');
      await loadIssuers(rid);
    } finally {
      setIssuerItemPecSending(false);
      setIssuerItemPecSendingId(null);
    }
  };

  const submitRichiestaDoc = async () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    const files = richiestaDocFiles || {};
    const existing = richiestaDocExisting;
    const hasExistingRequest = Boolean(existing?.id || existing?.is_draft);
    const hasAnyNew = richiestaDocAllTypes.some((type) => Boolean(files[type]));

    if (!hasAnyNew && hasExistingRequest) {
      closeRichiestaDocDialog();
      return;
    }
    if (!hasAnyNew) {
      toast.error(
        richiestaDocIsAzienda
          ? 'Seleziona almeno un file riconosciuto (contratto / mandato / doc id / visura / questionario)'
          : 'Seleziona almeno un file riconosciuto (contratto / mandato / doc id / questionario)'
      );
      return;
    }

    try {
      setRichiestaDocSaving(true);
      const form = new FormData();
      richiestaDocAllTypes.forEach((type) => {
        const file = files[type];
        if (!file) return;
        form.append('files', file);
        form.append('types', type);
      });
      const res = await api.post(`/dossier/${dossierId}/richieste-doc`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res?.data?.draft) {
        toast.success('Bozza salvata');
      } else {
        toast.success('Richiesta aggiornata');
      }
      await refreshRichiestaDocExisting(dossierId);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio richiesta');
    } finally {
      setRichiestaDocSaving(false);
    }
  };

  const deleteRichiestaDocFile = async (type) => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    try {
      setRichiestaDocDeleting(true);
      await api.delete(`/dossier/${dossierId}/richieste-doc/file/${type}`);
      toast.success('File eliminato');
      if (richiestaDocPreviewOpen && richiestaDocPreviewRow?.type === type) {
        setRichiestaDocPreviewOpen(false);
        setRichiestaDocPreviewRow(null);
        if (richiestaDocPreviewUrl) {
          window.URL.revokeObjectURL(richiestaDocPreviewUrl);
          setRichiestaDocPreviewUrl('');
        }
      }
      await refreshRichiestaDocExisting(dossierId);
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione file');
    } finally {
      setRichiestaDocDeleting(false);
    }
  };

  const deleteRichiestaDoc = async () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    try {
      setRichiestaDocDeleting(true);
      await api.delete(`/dossier/${dossierId}/richieste-doc`);
      toast.success('Richiesta eliminata');
      setRichiestaDocDeleteConfirmOpen(false);
      if (richiestaDocPreviewUrl) {
        window.URL.revokeObjectURL(richiestaDocPreviewUrl);
        setRichiestaDocPreviewUrl('');
      }
      setRichiestaDocPreviewOpen(false);
      setRichiestaDocPreviewLoading(false);
      setRichiestaDocPreviewRow(null);
      closeRichiestaDocDialog();
      fetchRows(page, rowsPerPage, search, stato);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione richiesta');
    } finally {
      setRichiestaDocDeleting(false);
    }
  };

  const revokeRichiestaDocPreviewUrl = () => {
    if (richiestaDocPreviewUrl) {
      window.URL.revokeObjectURL(richiestaDocPreviewUrl);
      setRichiestaDocPreviewUrl('');
    }
  };

  const handleCloseRichiestaDocPreview = () => {
    setRichiestaDocPreviewOpen(false);
    setRichiestaDocPreviewRow(null);
    revokeRichiestaDocPreviewUrl();
  };

  const handleOpenRichiestaDocPreview = async (type) => {
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }

    const localFile = richiestaDocFiles?.[type] || null;
    const existingName =
      type === 'contratto'
        ? richiestaDocExisting?.contratto_file_name
        : type === 'mandato'
          ? richiestaDocExisting?.mandato_file_name
          : type === 'doc_identita'
            ? richiestaDocExisting?.doc_identita_file_name
            : type === 'visura'
              ? richiestaDocExisting?.visura_file_name
              : richiestaDocExisting?.questionario_file_name;
    const existingSize =
      type === 'contratto'
        ? richiestaDocExisting?.contratto_file_size
        : type === 'mandato'
          ? richiestaDocExisting?.mandato_file_size
          : type === 'doc_identita'
            ? richiestaDocExisting?.doc_identita_file_size
            : type === 'visura'
              ? richiestaDocExisting?.visura_file_size
              : richiestaDocExisting?.questionario_file_size;

    if (!localFile && !existingName) {
      toast.warning('Nessun file da visualizzare');
      return;
    }

    setRichiestaDocPreviewRow({
      type,
      name: localFile?.name || existingName || type,
      mimeType: localFile?.type || guessMimeFromFilename(localFile?.name || existingName || ''),
      sizeBytes: localFile?.size != null ? Number(localFile.size) : Number(existingSize || 0),
      source: localFile ? 'local' : 'existing'
    });

    setRichiestaDocPreviewOpen(true);
    revokeRichiestaDocPreviewUrl();

    const mime = String(localFile?.type || guessMimeFromFilename(localFile?.name || existingName || '') || '').toLowerCase();
    const isPreviewable = mime.includes('pdf') || mime.startsWith('image/');
    if (!isPreviewable) {
      toast.warning("Formato file non supportato per l'anteprima");
      return;
    }

    try {
      setRichiestaDocPreviewLoading(true);
      if (localFile) {
        const url = window.URL.createObjectURL(localFile);
        setRichiestaDocPreviewUrl(url);
      } else {
        const res = await api.get(`/dossier/${dossierId}/richieste-doc/file/${type}/download`, {
          responseType: 'blob'
        });
        const blob = new Blob([res.data], { type: mime || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        setRichiestaDocPreviewUrl(url);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Errore durante il caricamento dell'anteprima");
    } finally {
      setRichiestaDocPreviewLoading(false);
    }
  };

  const handleOpenRichiestaDocPreviewInNewTab = async () => {
    if (!richiestaDocPreviewRow) return;
    const dossierId = Number(richiestaDocTarget?.dossier_id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) return;

    if (richiestaDocPreviewUrl) {
      window.open(richiestaDocPreviewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (richiestaDocPreviewRow.source === 'local') return;

    try {
      setRichiestaDocPreviewOpenInNewTabLoading(true);
      const res = await api.get(`/dossier/${dossierId}/richieste-doc/file/${richiestaDocPreviewRow.type}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: richiestaDocPreviewRow.mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Errore durante l'apertura del documento");
    } finally {
      setRichiestaDocPreviewOpenInNewTabLoading(false);
    }
  };

  const revokePreviewUrl = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
  };

  const handleOpenPreview = async (fileRow) => {
    setPreviewRow(fileRow);
    setPreviewOpen(true);
    revokePreviewUrl();
    const mime = String(fileRow?.mime_type || '').toLowerCase();
    const isPreviewable = mime.includes('pdf') || mime.startsWith('image/');
    if (!isPreviewable) {
      toast.warning('Formato file non supportato per l\'anteprima');
      return;
    }

    try {
      setPreviewLoading(true);
      const res = await api.get(`/richieste-doc/risposte-files/${fileRow.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: fileRow.mime_type || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error("Errore durante il caricamento dell'anteprima");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewRow(null);
    revokePreviewUrl();
  };

  const handleOpenPreviewInNewTab = async () => {
    if (!previewRow) return;
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      setOpenInNewTabLoading(true);
      const res = await api.get(`/richieste-doc/risposte-files/${previewRow.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: previewRow.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening file in new tab:', error);
      toast.error("Errore durante l'apertura del documento");
    } finally {
      setOpenInNewTabLoading(false);
    }
  };

  const headerIcon = useMemo(() => FileText, []);

  return (
    <Box sx={{ p: 3 }}>
      <input
        ref={legalDocFileRef}
        type="file"
        onChange={uploadLegalDocFile}
        style={{ display: 'none' }}
      />
      <input
        ref={rispostaFileRef}
        type="file"
        multiple
        onChange={handleRispostaFileSelected}
        style={{ display: 'none' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#2563eb14',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {(() => {
              const Icon = headerIcon;
              return <Icon size={20} />;
            })()}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Richieste Documenti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione centralizzata delle richieste documenti (contratto/mandato/doc identità) collegate ai dossier.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(5, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 2
        }}
      >
        {statoWidgets.map((w) => (
          <StatWidget
            key={w.key}
            title={w.title}
            value={statsLoading ? '…' : w.value}
            icon={w.Icon}
            color={w.color}
            active={stato === w.key}
            onClick={() => setStato((prev) => (prev === w.key ? 'all' : w.key))}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(2, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 2
        }}
      >
        {diffidaWidgets.map((w) => (
          <StatWidget
            key={`diffida_${w.key}`}
            title={w.title}
            value={statsLoading ? '…' : w.value}
            icon={w.Icon}
            color={w.color}
            active={diffidaFilter === w.key}
            onClick={() => setDiffidaFilter((prev) => (prev === w.key ? 'all' : w.key))}
          />
        ))}
      </Box>

      <StyledCard>
        <Grid container spacing={2} sx={{ p: 2, mb: 3, alignItems: 'center' }}>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              size="small"
              placeholder="Cerca dossier / cliente / commerciale / file..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="stato-richieste-doc-label">Stato</InputLabel>
              <Select
                labelId="stato-richieste-doc-label"
                value={stato}
                label="Stato"
                onChange={(e) => setStato(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="predisposta">Predisposta</MenuItem>
                <MenuItem value="pec_inviata_parziale">PEC inviata (parziale)</MenuItem>
                <MenuItem value="inviata">Inviata</MenuItem>
                <MenuItem value="pec_senza_risposta">PEC inviata senza risposta</MenuItem>
                <MenuItem value="non_gestire">Non gestire</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="diffida-richieste-doc-label">Diffida</InputLabel>
              <Select
                labelId="diffida-richieste-doc-label"
                value={diffidaFilter}
                label="Diffida"
                onChange={(e) => setDiffidaFilter(e.target.value)}
              >
                <MenuItem value="all">Tutte</MenuItem>
                <MenuItem value="inviata">Inviate</MenuItem>
                <MenuItem value="da_inviare">Da inviare</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ whiteSpace: 'normal', lineHeight: 1.1 }}>
                  <TableSortLabel
                    active={orderBy === 'data_conferimento'}
                    direction={orderBy === 'data_conferimento' ? order : 'asc'}
                    onClick={handleRequestSortDataConferimento}
                  >
                    <Box sx={{ lineHeight: 1.1 }}>
                      <span>Data</span>
                      <br />
                      <span>conferimento</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell sx={{ whiteSpace: 'normal', lineHeight: 1.1 }}>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={handleRequestSortCreatedAt}
                  >
                    <Box sx={{ lineHeight: 1.1 }}>
                      <span>Data</span>
                      <br />
                      <span>predisposizione</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Stato</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Dossier</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Resp. comm.</TableCell>
                <TableCell sx={{ width: 56, textAlign: 'center', whiteSpace: 'nowrap' }}>Doc.</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Creditori</TableCell>
                <TableCell sx={{ width: 64, textAlign: 'right' }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <LoaderProgress size={48} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    Nessuna richiesta trovata
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const richiestaId = Number(row?.id);
                  const dossierId = Number(row?.dossier_id);
                  const dossier = row?.dossier || null;
                  const pecTotal = Number(row?.issuer_manage_count || 0);
                  const pecSent = Number(row?.issuer_sent_count || 0);
                  const rawStato = String(row?.stato_richiesta || '').trim().toLowerCase();
                  const effectiveStato =
                    rawStato === 'non_gestire' ? 'non_gestire' : (pecTotal > 0 && pecSent >= pecTotal ? 'inviata' : 'predisposta');
                  const meta = richiestaDocStatusMeta(effectiveStato);
                  const StatusIcon = meta.Icon;
                  const statusChipLabel = meta.label.replace(/^Richiesta doc:\s*/i, '');
                  const isOpen = Boolean(expanded[richiestaId]);
                  const filesList = [
                    row?.contratto_file_name ? { key: 'contratto', label: 'Contratto', name: row.contratto_file_name } : null,
                    row?.mandato_file_name ? { key: 'mandato', label: 'Mandato', name: row.mandato_file_name } : null,
                    row?.doc_identita_file_name ? { key: 'doc', label: 'Doc', name: row.doc_identita_file_name } : null,
                    row?.visura_file_name && Boolean(String(dossier?.customer_ragione_sociale || '').trim())
                      ? { key: 'visura', label: 'Visura', name: row.visura_file_name }
                      : null,
                    row?.questionario_file_name ? { key: 'questionario', label: 'Questionario', name: row.questionario_file_name } : null
                  ].filter(Boolean);
                  const hasFiles = filesList.length > 0;

                  return (
                    <Fragment key={row.id}>
                      <TableRow hover sx={(theme) => ({ '&:hover td': { bgcolor: theme.palette.action.hover } })}>
                        <TableCell sx={{ width: 40 }}>
                          <IconButton size="small" onClick={() => toggleExpanded(richiestaId)}>
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {dossier?.data_conferimento ? formatDateOnly(dossier.data_conferimento) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: '#eff6ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <User size={18} color="#2563eb" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                                {getCustomerDisplayName(dossier)}
                              </Typography>
                              {dossier?.customer_telephone ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Phone size={14} color="#64748b" />
                                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                    {dossier.customer_telephone}
                                  </Typography>
                                </Box>
                              ) : null}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.15 }}
                              >
                                {dossier?.customer_email || '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography
                            variant="caption"
                            color={row?.created_at ? 'text.primary' : 'text.secondary'}
                            sx={{ display: 'block', lineHeight: 1.15 }}
                          >
                            {row?.created_at ? formatDateTime(row.created_at) : '-'}
                          </Typography>
                          {row?.created_at && (
                            <Tooltip
                              title={
                                effectiveStato === 'inviata' && row?.inviata_il
                                  ? 'Tempo di gestione (predisposizione → invio)'
                                  : 'Tempo trascorso dalla predisposizione'
                              }
                              arrow
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  lineHeight: 1.15,
                                  fontWeight: 800,
                                  color:
                                    effectiveStato === 'inviata'
                                      ? '#16a34a'
                                      : '#ef4444'
                                }}
                              >
                                {effectiveStato === 'inviata' && row?.inviata_il
                                  ? formatGestitaIn(row.created_at, row.inviata_il)
                                  : formatPredispostaDa(row.created_at)}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
                            <Tooltip title={meta.label}>
                              <Chip
                                size="small"
                                icon={<StatusIcon size={14} />}
                                label={statusChipLabel}
                                sx={{
                                  bgcolor: meta.bg,
                                  color: meta.color,
                                  fontWeight: 800,
                                  '& .MuiChip-icon': { color: meta.color }
                                }}
                              />
                            </Tooltip>
                            {(() => {
                              const show = Number.isFinite(pecTotal) && pecTotal > 0 && Number.isFinite(pecSent);
                              if (!show) return null;
                              const isDone = pecSent >= pecTotal;
                              const pecColor = isDone ? '#2563eb' : '#f59e0b';
                              const pecBg = isDone ? '#2563eb1A' : '#f59e0b1A';
                              return (
                                <Tooltip title="PEC inviate (creditori gestiti: esclusi Non gestire)">
                                  <Chip
                                    size="small"
                                    label={`PEC ${Math.max(0, pecSent)}/${Math.max(0, pecTotal)}`}
                                    sx={{
                                      bgcolor: pecBg,
                                      color: pecColor,
                                      fontWeight: 800
                                    }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            {Number.isInteger(dossierId) ? `#${dossierId}` : '-'}
                          </Typography>
                          {Array.isArray(row?.pratiche_tipologie) && row.pratiche_tipologie.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                              {Array.isArray(row?.pratiche_tipologie_counts) && row.pratiche_tipologie_counts.length > 0
                                ? row.pratiche_tipologie_counts
                                  .map((x) => `${String(x?.name || '').trim() || 'Non specificata'} (${Number(x?.cnt || 0) || 0})`)
                                  .join(' • ')
                                : row.pratiche_tipologie.join(', ')}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dossier?.responsabile_commerciale_nome || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 56, textAlign: 'center' }}>
                          <Tooltip
                            title={
                              hasFiles ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  {filesList.map((f) => (
                                    <Typography key={f.key} variant="caption" sx={{ lineHeight: 1.15 }}>
                                      {`${f.label}: ${f.name}`}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                'Nessun file'
                              )
                            }
                            arrow
                          >
                            <span>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '10px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: hasFiles ? '#16a34a1A' : '#ef44441A'
                                }}
                              >
                                <FileText size={18} color={hasFiles ? '#16a34a' : '#ef4444'} />
                              </Box>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Numero creditori">
                            <Chip size="small" variant="outlined" label={Number(row?.issuer_count || 0)} />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Modifica documenti dossier">
                            <IconButton size="small" onClick={() => openRichiestaDocDialog(row)}>
                              <Settings2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: 0 }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                  Creditori
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Plus size={16} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAddIssuerDrawer({ richiestaId });
                                  }}
                                  disabled={issuerLoading[richiestaId] || richiestaDocPecSending || issuerItemPecSending || richiestaDocSaving || richiestaDocDeleting || richiestaDocStatusSaving}
                                  sx={(theme) => ({
                                    fontWeight: 800,
                                    borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.22) : '#cbd5e1',
                                    '&:hover': { borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.32) : '#94a3b8' }
                                  })}
                                >
                                  Aggiungi
                                </Button>
                              </Box>
                              {issuerLoading[richiestaId] ? (
                                <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <CircularProgress size={22} />
                                </Box>
                              ) : (issuerRows[richiestaId] || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  Nessun creditore
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {(issuerRows[richiestaId] || []).map((it) => {
                                    const risposteList = Array.isArray(it?.risposte_files) ? it.risposte_files : [];
                                    const activeTipi = (risposteTipi || []).filter((t) => t?.active === 1 || t?.active === true);
                                    const contrattoTipoId = (() => {
                                      const byCode = activeTipi.find(
                                        (t) => String(t?.code || '').trim().toLowerCase() === 'contratto'
                                      );
                                      const byLabel = activeTipi.find(
                                        (t) => String(t?.label || '').trim().toLowerCase() === 'contratto'
                                      );
                                      const id = Number((byCode || byLabel)?.id);
                                      return Number.isInteger(id) && id > 0 ? id : null;
                                    })();
                                    const tipoOptions = (() => {
                                      const map = new Map();
                                      activeTipi.forEach((t) => {
                                        const id = Number(t?.id);
                                        if (!Number.isInteger(id) || id <= 0) return;
                                        map.set(id, { id, label: t?.label || `Tipo #${id}` });
                                      });
                                      risposteList.forEach((f) => {
                                        const id = f?.tipo_id != null ? Number(f.tipo_id) : null;
                                        if (!Number.isInteger(id) || id <= 0) return;
                                        if (map.has(id)) return;
                                        map.set(id, { id, label: f?.tipo_label || f?.tipo_code || `Tipo #${id}` });
                                      });
                                      return Array.from(map.values());
                                    })();
                                    const inboxCount = risposteList.filter((f) => f?.tipo_id == null).length;
                                    const issuerItemId = Number(it?.id);
                                    const isIssuerOpen = Number.isInteger(issuerItemId) ? Boolean(issuerExpanded[issuerItemId]) : false;
                                    return (
                                      <Accordion
                                        key={it.id}
                                        expanded={isIssuerOpen}
                                        onChange={(_event, next) => {
                                          if (!Number.isInteger(issuerItemId) || issuerItemId <= 0) return;
                                          setIssuerExpanded((prev) => ({ ...prev, [issuerItemId]: next }));
                                        }}
                                        disableGutters
                                        variant="outlined"
                                        sx={(theme) => ({
                                          borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0',
                                          borderRadius: '12px',
                                          overflow: 'hidden',
                                          '&:before': { display: 'none' }
                                        })}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ChevronDown size={18} />}
                                          sx={{
                                            minHeight: 44,
                                            px: 1,
                                            '& .MuiAccordionSummary-content': { my: 0.75 }
                                          }}
                                        >
                                          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                              <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                                                  {it?.issuer?.name || `Creditore #${it.issuer_id}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                                  {it?.issuer?.pec || it?.issuer?.direzione_generale || ''}
                                                </Typography>
                                                {it?.pratica?.category_name ? (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                    {`Pratica: ${it.pratica.category_name}`}
                                                    {it?.pratica?.reference ? ` • ${it.pratica.reference}` : ''}
                                                  </Typography>
                                                ) : null}
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Tooltip title="Invia PEC a questo creditore">
                                                  <span>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      startIcon={<Send size={16} />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openIssuerItemPecDialog({
                                                          richiestaId,
                                                          issuerItemId: it.id,
                                                          issuerName: it?.issuer?.name || `Creditore #${it.issuer_id}`,
                                                          issuerPec: it?.issuer?.pec || it?.issuer?.direzione_generale || '',
                                                          dossierId,
                                                          customerLabel: getCustomerDisplayName(dossier),
                                                          customerEmail: String(dossier?.customer_email || '').trim(),
                                                          attachments: [
                                                            row?.contratto_file_name ? { type: 'contratto', label: 'Contratto', name: row.contratto_file_name } : null,
                                                            row?.mandato_file_name ? { type: 'mandato', label: 'Mandato', name: row.mandato_file_name } : null,
                                                            row?.doc_identita_file_name ? { type: 'doc_identita', label: 'Doc identità', name: row.doc_identita_file_name } : null,
                                                            row?.visura_file_name && Boolean(String(dossier?.customer_ragione_sociale || '').trim())
                                                              ? { type: 'visura', label: 'Visura Azienda', name: row.visura_file_name }
                                                              : null,
                                                            row?.questionario_file_name
                                                              ? { type: 'questionario', label: 'Questionario', name: row.questionario_file_name }
                                                              : null
                                                          ].filter(Boolean)
                                                        });
                                                      }}
                                                      disabled={
                                                        String(it?.stato_item || '').toLowerCase() === 'non_gestire' ||
                                                        issuerItemPecSendingId === Number(it.id) ||
                                                        issuerItemPecSending
                                                      }
                                                      sx={(theme) => ({
                                                        fontWeight: 800,
                                                        borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.22) : '#cbd5e1',
                                                        color: theme.palette.mode === 'dark' ? '#93c5fd' : '#2563eb',
                                                        '& .MuiButton-startIcon': { mr: 0.5 },
                                                        '&:hover': { borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.32) : '#94a3b8' }
                                                      })}
                                                    >
                                                      Invia PEC
                                                    </Button>
                                                  </span>
                                                </Tooltip>
                                                <Tooltip title="Azioni creditore">
                                                  <span>
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) =>
                                                        openIssuerItemMenu(e, {
                                                          richiestaId,
                                                          issuerItemId: it.id,
                                                          item: it
                                                        })
                                                      }
                                                      disabled={issuerItemStatoSaving || issuerItemStatoSavingId === Number(it.id)}
                                                      sx={(theme) => ({
                                                        color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                                                        '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.10) }
                                                      })}
                                                    >
                                                      <MoreVertical size={16} />
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                                {(() => {
                                                  const meta = issuerItemStatusMeta(it?.stato_item);
                                                  const Icon = meta.Icon;
                                                  return (
                                                    <Chip
                                                      size="small"
                                                      label={meta.label}
                                                      icon={<Icon size={14} />}
                                                      sx={{
                                                        bgcolor: meta.bg,
                                                        color: meta.color,
                                                        fontWeight: 800,
                                                        '& .MuiChip-icon': { color: meta.color }
                                                      }}
                                                    />
                                                  );
                                                })()}
                                              </Box>
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                                              <Box sx={{ minWidth: 190 }} onClick={(e) => e.stopPropagation()}>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontWeight: 700, display: 'block', lineHeight: 1.15 }}
                                                >
                                                  Data invio
                                                </Typography>
                                                {issuerItemInvioEditId === Number(it?.id) ? (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                    <TextField
                                                      size="small"
                                                      type="datetime-local"
                                                      value={issuerItemInvioEditValue}
                                                      onChange={(event) => setIssuerItemInvioEditValue(event.target.value)}
                                                      InputLabelProps={{ shrink: true }}
                                                      sx={{ width: 210 }}
                                                    />
                                                    <Tooltip title="Svuota" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={() => setIssuerItemInvioEditValue('')}
                                                          disabled={issuerItemInvioSavingId === Number(it?.id)}
                                                        >
                                                          <Trash2 size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title="Salva" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          color="success"
                                                          onClick={() =>
                                                            saveIssuerItemInvioEdit({
                                                              richiestaId,
                                                              issuerItemId: it.id,
                                                              item: it
                                                            })
                                                          }
                                                          disabled={issuerItemInvioSavingId === Number(it?.id)}
                                                        >
                                                          <Check size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title="Annulla" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={cancelIssuerItemInvioEdit}
                                                          disabled={issuerItemInvioSavingId === Number(it?.id)}
                                                        >
                                                          <X size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                  </Box>
                                                ) : (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                      {it?.inviata_il ? formatDateTime(it.inviata_il) : '-'}
                                                    </Typography>
                                                    {String(it?.stato_item || '').toLowerCase() !== 'non_gestire' ? (
                                                      <Tooltip title="Modifica data invio" arrow>
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                              startIssuerItemInvioEdit({
                                                                richiestaId,
                                                                issuerItemId: it.id,
                                                                item: it
                                                              })
                                                            }
                                                            disabled={
                                                              issuerItemInvioSavingId === Number(it?.id) ||
                                                              issuerItemPecSending ||
                                                              richiestaDocPecSending ||
                                                              issuerItemStatoSaving ||
                                                              richiestaDocSaving ||
                                                              richiestaDocDeleting ||
                                                              richiestaDocStatusSaving
                                                            }
                                                            sx={(theme) => ({
                                                              color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                                                              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.10) }
                                                            })}
                                                          >
                                                            <Pencil size={14} />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                    ) : null}
                                                  </Box>
                                                )}
                                                {it?.inviata_il && issuerItemInvioEditId !== Number(it?.id) ? (
                                                  <Tooltip title="Tempo trascorso dall'invio" arrow>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                      {formatTimeAgo(it.inviata_il)}
                                                    </Typography>
                                                  </Tooltip>
                                                ) : null}
                                              </Box>
                                              <Box sx={{ minWidth: 190 }}>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontWeight: 700, display: 'block', lineHeight: 1.15 }}
                                                >
                                                  Data ricezione
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                  {it?.ricevuta_il ? formatDateTime(it.ricevuta_il) : '-'}
                                                </Typography>
                                              </Box>
                                              <Box sx={{ minWidth: 190 }}>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontWeight: 700, display: 'block', lineHeight: 1.15 }}
                                                >
                                                  Data sollecito
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                  {it?.ultimo_sollecito_il ? formatDateTime(it.ultimo_sollecito_il) : '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                  n. {Number(it?.sollecito_count || 0)}
                                                </Typography>
                                              </Box>
                                              <Box sx={{ minWidth: 190 }}>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontWeight: 700, display: 'block', lineHeight: 1.15 }}
                                                >
                                                  Data diffida
                                                </Typography>
                                                {issuerItemDiffidaEditId === Number(it?.id) ? (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }} onClick={(e) => e.stopPropagation()}>
                                                    <TextField
                                                      size="small"
                                                      type="datetime-local"
                                                      value={issuerItemDiffidaEditValue}
                                                      onChange={(event) => setIssuerItemDiffidaEditValue(event.target.value)}
                                                      InputLabelProps={{ shrink: true }}
                                                      sx={{ width: 210 }}
                                                    />
                                                    <Tooltip title="Svuota" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={() => setIssuerItemDiffidaEditValue('')}
                                                          disabled={issuerItemDiffidaSavingId === Number(it?.id)}
                                                        >
                                                          <Trash2 size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title="Salva" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          color="success"
                                                          onClick={() =>
                                                            saveIssuerItemDiffidaEdit({
                                                              richiestaId,
                                                              issuerItemId: it.id,
                                                              item: it
                                                            })
                                                          }
                                                          disabled={issuerItemDiffidaSavingId === Number(it?.id)}
                                                        >
                                                          <Check size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title="Annulla" arrow>
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={cancelIssuerItemDiffidaEdit}
                                                          disabled={issuerItemDiffidaSavingId === Number(it?.id)}
                                                        >
                                                          <X size={16} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                  </Box>
                                                ) : (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                      {it?.diffida_inviata_il ? formatDateTime(it.diffida_inviata_il) : '-'}
                                                    </Typography>
                                                    {String(it?.stato_item || '').toLowerCase() !== 'non_gestire' ? (
                                                      <Tooltip title="Modifica data diffida" arrow>
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                              startIssuerItemDiffidaEdit({
                                                                richiestaId,
                                                                issuerItemId: it.id,
                                                                item: it
                                                              })
                                                            }
                                                            disabled={
                                                              issuerItemDiffidaSavingId === Number(it?.id) ||
                                                              issuerItemPecSending ||
                                                              richiestaDocPecSending ||
                                                              issuerItemStatoSaving ||
                                                              richiestaDocSaving ||
                                                              richiestaDocDeleting ||
                                                              richiestaDocStatusSaving
                                                            }
                                                            sx={(theme) => ({
                                                              color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                                                              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.10) }
                                                            })}
                                                          >
                                                            <Pencil size={14} />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                    ) : null}
                                                  </Box>
                                                )}
                                                {it?.diffida_inviata_il && issuerItemDiffidaEditId !== Number(it?.id) ? (
                                                  <Tooltip title="Tempo trascorso dall'ultima diffida" arrow>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                      {formatTimeAgo(it.diffida_inviata_il)}
                                                    </Typography>
                                                  </Tooltip>
                                                ) : null}
                                              </Box>
                                              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Chip size="small" variant="outlined" label={`Totali: ${risposteList.length}`} />
                                                <Chip
                                                  size="small"
                                                  label={`Da classificare: ${inboxCount}`}
                                                  sx={{
                                                    bgcolor: inboxCount > 0 ? '#f59e0b1A' : '#64748b1A',
                                                    color: inboxCount > 0 ? '#f59e0b' : '#64748b',
                                                    fontWeight: 800
                                                  }}
                                                />
                                              </Box>
                                            </Box>

                                            {it?.stato_item === 'errore' && (it?.errore_messaggio || it?.errore_codice) && (
                                              <Box>
                                                <Typography variant="caption" color="error.main" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.15 }}>
                                                  Errore invio
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="error.main"
                                                  sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.15 }}
                                                >
                                                  {[it?.errore_codice, it?.errore_messaggio].filter(Boolean).join(' · ')}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 1, pt: 0, pb: 1.25 }}>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                              Documenti ricevuti
                                            </Typography>
                                            {(!it?.inviata_il || String(it?.stato_item || '').toLowerCase() === 'predisposta') &&
                                            String(it?.stato_item || '').toLowerCase() !== 'non_gestire' ? (
                                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                                Invia la PEC per abilitare la ricezione documenti.
                                              </Typography>
                                            ) : null}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Upload size={16} />}
                                                onClick={() => handlePickRispostaFiles(richiestaId, Number(it.id))}
                                                disabled={
                                                  rispostaDeleting ||
                                                  rispostaUploading ||
                                                  rispostaUpdating ||
                                                  String(it?.stato_item || '').toLowerCase() === 'non_gestire' ||
                                                  !it?.inviata_il ||
                                                  String(it?.stato_item || '').toLowerCase() === 'predisposta'
                                                }
                                                sx={(theme) => ({
                                                  fontWeight: 800,
                                                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.22) : '#cbd5e1',
                                                  '& .MuiButton-startIcon': { color: theme.palette.mode === 'dark' ? '#93c5fd' : '#2563eb' },
                                                  '&:hover': { borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.32) : '#94a3b8' }
                                                })}
                                              >
                                                Carica allegati
                                              </Button>
                                            </Box>

                                            {risposteList.length === 0 ? (
                                              <Typography variant="caption" color="text.secondary">
                                                Nessun documento ricevuto
                                              </Typography>
                                            ) : (
                                              <Table size="small" sx={{ mt: 0.5 }}>
                                            <TableHead
                                              sx={{
                                                '& th': {
                                                  bgcolor: 'transparent',
                                                  fontSize: 12,
                                                  fontWeight: 800,
                                                  color: 'text.secondary',
                                                  borderBottomColor: '#e2e8f0'
                                                }
                                              }}
                                            >
                                              <TableRow>
                                                <TableCell>File</TableCell>
                                                <TableCell sx={{ width: 220 }}>Tipo</TableCell>
                                                <TableCell sx={{ width: 170, whiteSpace: 'nowrap' }}>Caricato</TableCell>
                                                <TableCell sx={{ width: 240 }} align="right" />
                                              </TableRow>
                                            </TableHead>
                                            <TableBody
                                              sx={{
                                                '& td': {
                                                  fontSize: 13,
                                                  borderBottomColor: '#e2e8f0'
                                                }
                                              }}
                                            >
                                              {risposteList.map((f) => (
                                                <TableRow key={f.id} hover>
                                                  <TableCell sx={{ minWidth: 0 }}>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{ fontWeight: 700, lineHeight: 1.15, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                    >
                                                      {f?.original_name || `File #${f.id}`}
                                                    </Typography>
                                                    {f?.mime_type && (
                                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                        {f.mime_type}
                                                      </Typography>
                                                    )}
                                                  </TableCell>
                                                  <TableCell>
                                                    <FormControl size="small" fullWidth>
                                                      <Select
                                                        value={f?.tipo_id != null ? String(f.tipo_id) : ''}
                                                        onChange={(e) =>
                                                          handleUpdateRispostaFileTipo(richiestaId, Number(f.id), String(e.target.value || ''))
                                                        }
                                                        displayEmpty
                                                        disabled={rispostaDeleting || rispostaUploading || rispostaUpdating}
                                                      >
                                                        <MenuItem value="">
                                                          <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>
                                                            Da classificare
                                                          </Typography>
                                                        </MenuItem>
                                                        {tipoOptions.map((opt) => (
                                                          <MenuItem key={opt.id} value={String(opt.id)}>
                                                            {opt.label}
                                                          </MenuItem>
                                                        ))}
                                                      </Select>
                                                    </FormControl>
                                                  </TableCell>
                                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.15 }}>
                                                      {f?.created_at ? formatDateTime(f.created_at) : '-'}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                      {(() => {
                                                        const fallbackLabel = String(f?.tipo_label || f?.tipo_code || '').trim().toLowerCase();
                                                        const isContratto =
                                                          contrattoTipoId != null
                                                            ? Number(f?.tipo_id) === contrattoTipoId
                                                            : fallbackLabel === 'contratto';
                                                        if (!isContratto) return null;
                                                        return (
                                                          <Tooltip title="Invia DIFFIDA a questo creditore">
                                                            <span>
                                                              <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<AlertTriangle size={16} />}
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  openIssuerItemPecDialog({
                                                                    kind: 'diffida',
                                                                    richiestaId,
                                                                    issuerItemId: it.id,
                                                                    issuerName: it?.issuer?.name || `Creditore #${it.issuer_id}`,
                                                                    issuerPec: it?.issuer?.pec || it?.issuer?.direzione_generale || '',
                                                                    dossierId,
                                                                    customerLabel: getCustomerDisplayName(dossier),
                                                                    customerEmail: String(dossier?.customer_email || '').trim(),
                                                                    attachments: [
                                                                      row?.contratto_file_name
                                                                        ? { type: 'contratto', label: 'Contratto', name: row.contratto_file_name }
                                                                        : null,
                                                                      row?.mandato_file_name ? { type: 'mandato', label: 'Mandato', name: row.mandato_file_name } : null,
                                                                      row?.doc_identita_file_name
                                                                        ? { type: 'doc_identita', label: 'Doc identità', name: row.doc_identita_file_name }
                                                                        : null,
                                                                      row?.visura_file_name && Boolean(String(dossier?.customer_ragione_sociale || '').trim())
                                                                        ? { type: 'visura', label: 'Visura Azienda', name: row.visura_file_name }
                                                                        : null,
                                                                      row?.questionario_file_name
                                                                        ? { type: 'questionario', label: 'Questionario', name: row.questionario_file_name }
                                                                        : null
                                                                    ].filter(Boolean)
                                                                  });
                                                                }}
                                                                disabled={
                                                                  rispostaDeleting ||
                                                                  rispostaUploading ||
                                                                  rispostaUpdating ||
                                                                  String(it?.stato_item || '').toLowerCase() === 'non_gestire' ||
                                                                  String(it?.stato_item || '').toLowerCase() === 'predisposta' ||
                                                                  !it?.inviata_il ||
                                                                  issuerItemPecSendingId === Number(it.id) ||
                                                                  issuerItemPecSending
                                                                }
                                                                sx={(theme) => ({
                                                                  height: 30,
                                                                  px: 1.25,
                                                                  minWidth: 0,
                                                                  fontWeight: 900,
                                                                  textTransform: 'none',
                                                                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.22) : '#fed7aa',
                                                                  color: theme.palette.mode === 'dark' ? '#fdba74' : '#ea580c',
                                                                  '& .MuiButton-startIcon': { mr: 0.5 }
                                                                })}
                                                              >
                                                                Invia DIFFIDA
                                                              </Button>
                                                            </span>
                                                          </Tooltip>
                                                        );
                                                      })()}
                                                      <Tooltip title="Anteprima documento">
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenPreview(f)}
                                                            disabled={rispostaDeleting || rispostaUploading || rispostaUpdating}
                                                            sx={(theme) => ({
                                                              mr: 0.5,
                                                              color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                                                              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.10) }
                                                            })}
                                                          >
                                                            <VisibilityIcon fontSize="small" />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                      <Tooltip title="Elimina documento">
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteRispostaFile(richiestaId, Number(f.id))}
                                                            disabled={rispostaDeleting || rispostaUploading || rispostaUpdating}
                                                            sx={(theme) => ({
                                                              color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626',
                                                              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#dc2626', 0.22) : alpha('#dc2626', 0.10) }
                                                            })}
                                                          >
                                                            <Trash2 size={16} />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                    </Box>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        )}
                                          </Box>
                                        </AccordionDetails>
                                      </Accordion>
                                  );
                                  })}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
          }
        />
      </StyledCard>

      <Menu
        anchorEl={issuerItemMenuAnchorEl}
        open={Boolean(issuerItemMenuAnchorEl)}
        onClose={closeIssuerItemMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {String(issuerItemMenuCtx?.item?.stato_item || '').toLowerCase() === 'non_gestire' ? (
          <MenuItem
            onClick={() => updateIssuerItemStato(issuerItemMenuCtx, getIssuerItemRestoreStato(issuerItemMenuCtx?.item))}
            disabled={issuerItemStatoSaving}
          >
            Ripristina gestione
          </MenuItem>
        ) : (
          <MenuItem onClick={() => updateIssuerItemStato(issuerItemMenuCtx, 'non_gestire')} disabled={issuerItemStatoSaving}>
            Imposta non gestire
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={richiestaDocDialogOpen}
        onClose={closeRichiestaDocDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FileText size={18} color="#2563eb" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Modifica documenti dossier
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gestisci file richiesta
              </Typography>
            </Box>
          </Box>
          {richiestaDocIsPartial && (
            <Tooltip title={`Caricati parzialmente (${richiestaDocPresentCount}/${richiestaDocRequiredTotal})`} arrow>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#f59e0b1F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <AlertTriangle size={18} color="#f59e0b" />
              </Box>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent
          dividers
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200, flex: '0 1 220px' }}>
              <InputLabel id="stato-richiesta-doc-label">Stato globale richiesta</InputLabel>
              <Select
                labelId="stato-richiesta-doc-label"
                value={String(richiestaDocTarget?.stato_richiesta || 'predisposta')}
                label="Stato globale richiesta"
                onChange={(e) => handleUpdateRichiestaStato(e.target.value)}
                disabled={richiestaDocStatusSaving || richiestaDocSaving || richiestaDocDeleting || richiestaDocPecSending}
              >
                <MenuItem value="predisposta">Predisposta</MenuItem>
                <MenuItem value="inviata">Inviata</MenuItem>
                <MenuItem value="non_gestire">Non gestire</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Seleziona uno o più file. Il sistema assegna automaticamente il tipo in base al nome:{' '}
              {richiestaDocIsAzienda ? 'contratto / mandato / doc id / visura / questionario' : 'contratto / mandato / doc id / questionario'}.
              Il nome viene mantenuto identico su Wasabi.
            </Typography>
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ maxWidth: 360 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
                    Suggerimenti
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - Il tipo viene riconosciuto dal nome file (
                    {richiestaDocIsAzienda ? 'contratto / mandato / doc id / visura / questionario' : 'contratto / mandato / doc id / questionario'}).
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - L’anteprima supporta PDF e immagini.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - “Elimina” rimuove il file già caricato su Wasabi.
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.5, mt: '2px' }}>
                <HelpCircle size={16} />
              </IconButton>
            </Tooltip>
          </Box>

          <input
            ref={richiestaDocFileRef}
            type="file"
            multiple
            onChange={handleRichiestaDocFilesSelected}
            style={{ display: 'none' }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {getCustomerDisplayName(richiestaDocTarget?.dossier || null)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dossier ID: {richiestaDocTarget?.dossier_id != null ? String(richiestaDocTarget.dossier_id) : '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {richiestaDocExistingLoading && <CircularProgress size={18} />}
              <Button
                variant="contained"
                onClick={handlePickRichiestaDocFiles}
                startIcon={<FileText size={16} />}
                disabled={richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
                sx={{ borderRadius: '8px' }}
              >
                Seleziona file
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {richiestaDocAllTypes.map((type) => {
              const file = richiestaDocFiles?.[type] || null;
              const isOptional = richiestaDocOptionalTypes.includes(type);
              const existingName =
                type === 'contratto'
                  ? richiestaDocExisting?.contratto_file_name
                  : type === 'mandato'
                    ? richiestaDocExisting?.mandato_file_name
                    : type === 'doc_identita'
                      ? richiestaDocExisting?.doc_identita_file_name
                      : type === 'visura'
                        ? richiestaDocExisting?.visura_file_name
                        : richiestaDocExisting?.questionario_file_name;
              const displayName = file?.name || existingName || 'Non selezionato';
              const chipLabel = file ? 'Nuovo' : existingName ? 'Caricato' : '—';
              const chipColor = file ? 'warning' : existingName ? 'success' : 'default';
              return (
                <Paper
                  key={type}
                  variant="outlined"
                  sx={(theme) => ({
                    p: 1.25,
                    borderRadius: '8px',
                    borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0'
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {richiestaDocTypeLabel(type)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={file || existingName ? 'text.primary' : 'text.secondary'}
                        sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {displayName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {isOptional && <Chip size="small" label="Opzionale" color="default" variant="outlined" />}
                      <Chip size="small" label={chipLabel} color={chipColor} variant={file || existingName ? 'filled' : 'outlined'} />
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          file
                            ? 'File selezionato ora (non ancora salvato su Wasabi)'
                            : existingName
                              ? 'File già caricato su Wasabi'
                              : 'Nessun file associato'
                        }
                      >
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <HelpCircle size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Apri anteprima">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRichiestaDocPreview(type)}
                            disabled={!Boolean(file || existingName) || richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
                            sx={(theme) => ({
                              p: 0.5,
                              color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.10) }
                            })}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!file && existingName && (
                        <Tooltip arrow placement="top" title="Elimina definitivamente il file da Wasabi">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Elimina"
                              onClick={() => deleteRichiestaDocFile(type)}
                              disabled={richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
                              sx={(theme) => ({
                                color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626',
                                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#dc2626', 0.22) : alpha('#dc2626', 0.10) }
                              })}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {Object.keys(richiestaDocDuplicates || {}).filter((k) => (richiestaDocDuplicates?.[k] || []).length > 0).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                File duplicati ignorati (per tipo viene usato il primo trovato)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {Object.entries(richiestaDocDuplicates || {})
                  .filter(([, list]) => Array.isArray(list) && list.length > 0)
                  .map(([type, list]) => (
                    <Typography key={type} variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {richiestaDocTypeLabel(type)}: {(list || []).map((f) => f?.name).filter(Boolean).join(', ')}
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}

          {Array.isArray(richiestaDocUnmatched) && richiestaDocUnmatched.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                File non riconosciuti (nome non contiene {richiestaDocIsAzienda ? 'contratto/mandato/doc id/visura/questionario' : 'contratto/mandato/doc id/questionario'})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {richiestaDocUnmatched.map((f, idx) => (
                  <Typography key={`${f?.name || 'file'}-${idx}`} variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {f?.name || 'file'}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {richiestaDocExisting?.id && (
            <Button
              color="error"
              onClick={() => setRichiestaDocDeleteConfirmOpen(true)}
              disabled={richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
              sx={{ mr: 'auto', borderRadius: '8px' }}
            >
              Elimina richiesta
            </Button>
          )}
          <Button onClick={closeRichiestaDocDialog} disabled={richiestaDocSaving || richiestaDocDeleting} sx={{ borderRadius: '8px' }}>
            Annulla
          </Button>
          <Button
            onClick={submitRichiestaDoc}
            variant="contained"
            disabled={
              richiestaDocSaving ||
              richiestaDocDeleting ||
              richiestaDocExistingLoading ||
              (!richiestaDocAllTypes.some((type) => Boolean(richiestaDocFiles?.[type])) &&
                !Boolean(richiestaDocExisting?.id || richiestaDocExisting?.is_draft))
            }
            sx={{ borderRadius: '8px' }}
          >
            {(() => {
              if (richiestaDocSaving) return 'Salvataggio...';
              const hasAnyNew = richiestaDocAllTypes.some((type) => Boolean(richiestaDocFiles?.[type]));
              if (hasAnyNew) return 'Salva';
              if (richiestaDocExisting?.id || richiestaDocExisting?.is_draft) return 'Chiudi';
              return 'Conferma';
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issuerItemPecDialogOpen} onClose={closeIssuerItemPecDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: issuerItemPecKind === 'diffida' ? '#fff7ed' : '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {issuerItemPecKind === 'diffida' ? <AlertTriangle size={18} color="#ea580c" /> : <Send size={18} color="#2563eb" />}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {issuerItemPecKind === 'diffida' ? 'Invio DIFFIDA' : 'Invio PEC'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {issuerItemPecKind === 'diffida'
                ? 'Invia una diffida via PEC: scegli il modello, personalizza testo e seleziona gli allegati.'
                : 'Modifica mittente, destinatari, oggetto e testo. Seleziona gli allegati per questo invio.'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="issuer-item-pec-account-label">Mittente (PEC)</InputLabel>
              <Select
                labelId="issuer-item-pec-account-label"
                value={String(issuerItemPecForm?.accountId || '')}
                label="Mittente (PEC)"
                onChange={(e) => setIssuerItemPecForm((p) => ({ ...p, accountId: String(e.target.value || '') }))}
                disabled={pecAccountsLoading || issuerItemPecSending}
              >
                {(pecAccounts || []).map((acc) => (
                  <MenuItem key={acc.id} value={String(acc.id)}>
                    {`${acc.name || ''}${acc.email ? ` <${acc.email}>` : ''}`.trim() || `PEC #${acc.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Destinatario"
              placeholder="es. pec@dominio.it"
              value={issuerItemPecForm?.to || ''}
              onChange={(e) => setIssuerItemPecForm((p) => ({ ...p, to: e.target.value }))}
              fullWidth
              disabled={issuerItemPecSending}
            />
            <TextField
              size="small"
              label="CC"
              placeholder="Facoltativo (separati da virgola)"
              value={issuerItemPecForm?.cc || ''}
              onChange={(e) => setIssuerItemPecForm((p) => ({ ...p, cc: e.target.value }))}
              fullWidth
              disabled={issuerItemPecSending}
            />
            <TextField
              size="small"
              label="CCN (cliente)"
              placeholder="Email cliente (separati da virgola)"
              value={issuerItemPecForm?.bcc || ''}
              onChange={(e) => setIssuerItemPecForm((p) => ({ ...p, bcc: e.target.value }))}
              fullWidth
              disabled={issuerItemPecSending}
            />
            <TextField
              size="small"
              label="Oggetto"
              value={issuerItemPecForm?.subject || ''}
              onChange={(e) => setIssuerItemPecForm((p) => ({ ...p, subject: e.target.value }))}
              fullWidth
              disabled={issuerItemPecSending}
            />

            <Autocomplete
              size="small"
              options={modelli || []}
              getOptionLabel={(option) => option.nome_modello || ''}
              value={(modelli || []).find((m) => String(m.id) === String(issuerItemPecForm?.modelloId)) || null}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              disabled={modelliLoading || issuerItemPecSending}
              onChange={async (e, newValue) => {
                const modId = newValue ? newValue.id : '';
                if (!modId) {
                  setIssuerItemPecForm((p) => ({ ...p, modelloId: '', modelloName: '', modelloContent: '' }));
                  return;
                }
                
                try {
                  setModelliLoading(true);
                  const res = await api.get(`/modulistica/${modId}`);
                  const data = res.data;
                  let finalBody = data.content || '';
                  if (issuerItemPecCtx?.customerLabel) {
                    finalBody = finalBody.replace(/{cliente}/g, issuerItemPecCtx.customerLabel);
                  }
                  finalBody = finalBody.replace(/{data_corrente}/g, new Date().toLocaleDateString('it-IT'));
                  
                  setIssuerItemPecForm((p) => ({
                    ...p,
                    modelloId: modId,
                    modelloName: data.nome_modello || newValue.nome_modello || 'modello',
                    modelloContent: finalBody
                  }));
                } catch (error) {
                  console.error('Errore caricamento dettaglio modello:', error);
                  toast.error('Impossibile caricare il modello');
                } finally {
                  setModelliLoading(false);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={issuerItemPecKind === 'diffida' ? 'Modello Diffida' : 'Modello'}
                  placeholder="Cerca e seleziona un modello..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {modelliLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Tabs
              value={issuerItemPecTab}
              onChange={(e, v) => setIssuerItemPecTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label="Messaggio PEC" />
              <Tab label="Allegati" />
              <Tab label="Allegato Modello" disabled={!issuerItemPecForm?.modelloId} />
            </Tabs>

            {issuerItemPecTab === 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Corpo PEC
                </Typography>
                <Box sx={{ '& .ql-container': { minHeight: 200 } }}>
                  <ReactQuill
                    theme="snow"
                    value={issuerItemPecForm?.body || ''}
                    onChange={(value) => setIssuerItemPecForm((p) => ({ ...p, body: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={issuerItemPecSending}
                  />
                </Box>
              </Box>
            )}

            {issuerItemPecTab === 1 && (
              <Box>
                {richiesteDocSettings?.legal_doc_file_path ? (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Allegati obbligatori
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox checked disabled />}
                      label={
                        <Typography variant="caption" sx={{ fontWeight: 800 }}>
                          {`Doc. identità legale: ${String(richiesteDocSettings?.legal_doc_file_name || '').trim() || 'documento_identita_legale'}`}
                        </Typography>
                      }
                    />
                  </Box>
                ) : null}

                {(issuerItemPecCtx?.attachments || []).length > 0 ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Seleziona gli allegati da inviare
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {(issuerItemPecCtx?.attachments || []).map((a) => {
                        const type = String(a?.type || '').trim().toLowerCase();
                        const checked = issuerItemPecAttachmentTypes.includes(type);
                        return (
                          <FormControlLabel
                            key={`${type || 'file'}-${a?.name || ''}`}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked;
                                  setIssuerItemPecAttachmentTypes((prev) => {
                                    const list = Array.isArray(prev) ? prev : [];
                                    const cleaned = list.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);
                                    if (!type) return cleaned;
                                    if (next) return Array.from(new Set([...cleaned, type]));
                                    return cleaned.filter((x) => x !== type);
                                  });
                                }}
                                disabled={issuerItemPecSending}
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {`${a?.label || 'Allegato'}: ${a?.name || ''}`.trim()}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Box>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Nessun allegato selezionabile
                  </Typography>
                )}
              </Box>
            )}

            {issuerItemPecTab === 2 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Contenuto Modello (Verrà convertito in PDF e allegato)
                </Typography>
                <Box sx={{ '& .ql-container': { minHeight: 200 } }}>
                  <ReactQuill
                    theme="snow"
                    value={issuerItemPecForm?.modelloContent || ''}
                    onChange={(value) => setIssuerItemPecForm((p) => ({ ...p, modelloContent: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={issuerItemPecSending}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIssuerItemPecDialog} disabled={issuerItemPecSending}>
            Annulla
          </Button>
          <Button
            onClick={submitIssuerItemPecDialog}
            variant="contained"
            disabled={issuerItemPecSending}
            startIcon={issuerItemPecSending ? <CircularProgress size={18} color="inherit" /> : <Send size={18} />}
          >
            Invia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={richiestaDocDeleteConfirmOpen}
        onClose={() => !richiestaDocDeleting && setRichiestaDocDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Elimina richiesta</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Verranno eliminati i riferimenti della richiesta e tutti i file collegati su Wasabi.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRichiestaDocDeleteConfirmOpen(false)} disabled={richiestaDocDeleting}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteRichiestaDoc}
            disabled={richiestaDocDeleting}
          >
            {richiestaDocDeleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={risposteTipiDialogOpen}
        onClose={closeRisposteTipiDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Settings2 size={20} color="#2563eb" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                Parametri
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                Tipi documenti ricevuti
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={closeRisposteTipiDialog}
            disabled={risposteTipiSaving}
            sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
          >
            Chiudi
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: '16px',
              borderColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0')
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  Impostazioni Richieste Documenti
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                  Configurazione globale (documento identità legale, creditore di default, gestione incompleti)
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={saveRichiesteDocSettings}
                disabled={richiesteDocSettingsLoading || richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                sx={{ height: 36, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                startIcon={
                  richiesteDocSettingsSaving ? <CircularProgress size={18} color="inherit" /> : <Check size={18} />
                }
              >
                Salva
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(richiesteDocSettingsAllowIncomplete)}
                    onChange={(e) => setRichiesteDocSettingsAllowIncomplete(e.target.checked)}
                    disabled={richiesteDocSettingsLoading || richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                  />
                }
                label="Consenti generazione anche se incompleta"
              />

              <Autocomplete
                size="small"
                disablePortal
                options={richiesteDocSettingsIssuerOptions || []}
                loading={richiesteDocSettingsIssuerLoading}
                value={richiesteDocSettingsDefaultIssuer}
                onChange={(_e, v) => setRichiesteDocSettingsDefaultIssuer(v)}
                inputValue={richiesteDocSettingsIssuerQuery}
                onInputChange={(_e, v) => setRichiesteDocSettingsIssuerQuery(v)}
                getOptionLabel={(opt) => {
                  const name = String(opt?.name || '').trim();
                  const pec = String(opt?.pec || '').trim();
                  const id = opt?.id != null ? String(opt.id) : '';
                  if (name && pec) return `${name} · ${pec}`;
                  if (name) return name;
                  if (pec) return pec;
                  return id ? `Issuer #${id}` : '';
                }}
                isOptionEqualToValue={(opt, v) => String(opt?.id) === String(v?.id)}
                sx={{ minWidth: 360, flex: '1 1 360px' }}
                disabled={richiesteDocSettingsLoading || richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Creditore di default"
                    placeholder="Cerca per nome/PEC/ID..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {richiesteDocSettingsIssuerLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Doc. identità legale:
                </Typography>
                <Chip
                  size="small"
                  label={richiesteDocSettings?.legal_doc_file_name ? String(richiesteDocSettings.legal_doc_file_name) : 'Non configurato'}
                  sx={{
                    bgcolor: richiesteDocSettings?.legal_doc_file_name ? '#10b9811A' : '#ef44441A',
                    color: richiesteDocSettings?.legal_doc_file_name ? '#10b981' : '#ef4444',
                    fontWeight: 800
                  }}
                />
                <Tooltip title="Carica/aggiorna">
                  <span>
                    <IconButton
                      size="small"
                      onClick={pickLegalDocFile}
                      disabled={richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                      sx={{ borderRadius: '12px' }}
                    >
                      {richiesteDocSettingsLegalUploading ? <CircularProgress size={16} /> : <Upload size={16} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Scarica">
                  <span>
                    <IconButton
                      size="small"
                      onClick={downloadLegalDocFile}
                      disabled={!richiesteDocSettings?.legal_doc_file_path || richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                      sx={{ borderRadius: '12px' }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Rimuovi">
                  <span>
                    <IconButton
                      size="small"
                      onClick={clearLegalDocFile}
                      disabled={!richiesteDocSettings?.legal_doc_file_path || richiesteDocSettingsSaving || richiesteDocSettingsLegalUploading}
                      sx={{ borderRadius: '12px' }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: '16px',
              borderColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0')
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Cerca per code o label..."
                value={risposteTipiSearch}
                onChange={(e) => setRisposteTipiSearch(e.target.value)}
                sx={{ flex: '1 1 320px', minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  )
                }}
                disabled={risposteTipiSaving}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(risposteTipiOnlyActive)}
                    onChange={(e) => setRisposteTipiOnlyActive(e.target.checked)}
                    disabled={risposteTipiSaving}
                  />
                }
                label="Solo attivi"
              />
              <Button
                variant="contained"
                onClick={() => setRisposteTipiNewOpen((p) => !p)}
                disabled={risposteTipiSaving}
                sx={{ height: 36, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                startIcon={<Plus size={18} />}
              >
                Nuovo tipo
              </Button>
            </Box>

            <Collapse in={risposteTipiNewOpen} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'nowrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Code"
                  value={newTipo.code}
                  onChange={(e) => setNewTipo((p) => ({ ...p, code: e.target.value }))}
                  sx={{ width: 180 }}
                  disabled={risposteTipiSaving}
                />
                <TextField
                  size="small"
                  label="Label"
                  value={newTipo.label}
                  onChange={(e) => setNewTipo((p) => ({ ...p, label: e.target.value }))}
                  sx={{ flex: 1, minWidth: 220 }}
                  disabled={risposteTipiSaving}
                />
                <TextField
                  size="small"
                  label="Ordine"
                  type="number"
                  value={String(newTipo.sort_order)}
                  onChange={(e) => setNewTipo((p) => ({ ...p, sort_order: parseInt(e.target.value, 10) || 0 }))}
                  sx={{ width: 110 }}
                  disabled={risposteTipiSaving}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(newTipo.required)}
                      onChange={(e) => setNewTipo((p) => ({ ...p, required: e.target.checked }))}
                      disabled={risposteTipiSaving}
                    />
                  }
                  label="Obbl."
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(newTipo.active)}
                      onChange={(e) => setNewTipo((p) => ({ ...p, active: e.target.checked }))}
                      disabled={risposteTipiSaving}
                    />
                  }
                  label="Attivo"
                />
                <Button
                  variant="contained"
                  onClick={handleCreateTipo}
                  disabled={risposteTipiSaving}
                  sx={{ height: 36, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                >
                  Aggiungi
                </Button>
              </Box>
            </Collapse>
          </Paper>

          <Paper
            variant="outlined"
            sx={(theme) => ({
              borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0',
              borderRadius: '16px',
              overflow: 'hidden'
            })}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Code</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right" sx={{ width: 110, whiteSpace: 'nowrap' }}>
                    Ordine
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>
                    Obbl.
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>
                    Attivo
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {risposteTipiLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : risposteTipi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      Nessun tipo configurato
                    </TableCell>
                  </TableRow>
                ) : filteredRisposteTipi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      Nessun risultato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRisposteTipi.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={t.code}
                          sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontWeight: 800
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={String(t.label || '')}
                          onChange={(e) =>
                            setRisposteTipi((prev) =>
                              prev.map((x) => (x.id === t.id ? { ...x, label: e.target.value } : x))
                            )
                          }
                          disabled={risposteTipiSaving}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={String(t.sort_order ?? 0)}
                          onChange={(e) =>
                            setRisposteTipi((prev) =>
                              prev.map((x) =>
                                x.id === t.id ? { ...x, sort_order: parseInt(e.target.value, 10) || 0 } : x
                              )
                            )
                          }
                          disabled={risposteTipiSaving}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={Boolean(t.required)}
                          onChange={(e) =>
                            setRisposteTipi((prev) =>
                              prev.map((x) => (x.id === t.id ? { ...x, required: e.target.checked ? 1 : 0 } : x))
                            )
                          }
                          disabled={risposteTipiSaving}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={Boolean(t.active)}
                          onChange={(e) =>
                            setRisposteTipi((prev) =>
                              prev.map((x) => (x.id === t.id ? { ...x, active: e.target.checked ? 1 : 0 } : x))
                            )
                          }
                          disabled={risposteTipiSaving}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Salva">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdateTipo(t, {
                                  label: String(t.label || '').trim(),
                                  sort_order: Number(t.sort_order || 0),
                                  required: Boolean(t.required),
                                  active: Boolean(t.active)
                                })
                              }
                              disabled={risposteTipiSaving}
                              sx={{
                                borderRadius: '12px',
                                bgcolor: 'rgba(37, 99, 235, 0.10)',
                                color: '#2563eb',
                                '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.16)' }
                              }}
                            >
                              {risposteTipiSaving ? <CircularProgress size={16} color="inherit" /> : <Check size={16} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {filteredRisposteTipi.length} risultati
          </Typography>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={addIssuerDrawerOpen}
        onClose={closeAddIssuerDrawer}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 520 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <User size={18} color="#2563eb" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  Aggiungi creditore
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                  Seleziona un creditore già presente in anagrafica
                </Typography>
              </Box>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={closeAddIssuerDrawer}
              disabled={addIssuerSaving}
              sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
            >
              Chiudi
            </Button>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Autocomplete
              value={addIssuerSelected}
              onChange={(_event, next) => setAddIssuerSelected(next || null)}
              options={Array.isArray(addIssuerOptions) ? addIssuerOptions : []}
              loading={addIssuerLoading}
              inputValue={addIssuerQuery}
              onInputChange={(_event, nextValue) => setAddIssuerQuery(nextValue)}
              openOnFocus
              disablePortal
              noOptionsText={String(addIssuerQuery || '').trim().length === 1 ? 'Digita almeno 2 caratteri' : 'Nessun creditore trovato'}
              loadingText="Ricerca in corso..."
              getOptionLabel={(option) => {
                const name = String(option?.name || '').trim();
                const pec = String(option?.pec || option?.direzione_generale || '').trim();
                if (name && pec) return `${name} · ${pec}`;
                return name || pec || `Creditore #${option?.id || ''}`;
              }}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cerca creditore"
                  placeholder="Nome / PEC / ID"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addIssuerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />

            {addIssuerError ? (
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                {addIssuerError}
              </Typography>
            ) : null}

            {addIssuerSelected ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  borderColor: 'rgba(145, 158, 171, 0.24)',
                  bgcolor: '#f8fafc'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  {String(addIssuerSelected?.name || '').trim() || `Creditore #${addIssuerSelected?.id || '-'}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                  {String(addIssuerSelected?.pec || addIssuerSelected?.direzione_generale || '').trim() || '-'}
                </Typography>
              </Paper>
            ) : null}

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={closeAddIssuerDrawer} disabled={addIssuerSaving}>
                Annulla
              </Button>
              <Button variant="contained" onClick={submitAddIssuer} disabled={addIssuerSaving || !addIssuerSelected}>
                {addIssuerSaving ? 'Aggiunta...' : 'Aggiungi'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={richiestaDocPreviewOpen}
        onClose={handleCloseRichiestaDocPreview}
        hideBackdrop
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Anteprima documento
              </Typography>
              <Tooltip arrow placement="bottom" title="Supporta PDF e immagini. Usa “Apri in nuova scheda” per aprire a schermo intero.">
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <HelpCircle size={16} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExternalLink size={14} />}
                onClick={handleOpenRichiestaDocPreviewInNewTab}
                disabled={!richiestaDocPreviewRow || richiestaDocPreviewOpenInNewTabLoading}
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 2,
                  borderRadius: '12px',
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                {richiestaDocPreviewOpenInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleCloseRichiestaDocPreview}
                sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
              >
                Chiudi
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              {richiestaDocPreviewLoading && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!richiestaDocPreviewLoading && richiestaDocPreviewRow && (() => {
                const mime = String(richiestaDocPreviewRow.mimeType || '').toLowerCase();
                const isPdf = mime.includes('pdf');
                const isImage = mime.startsWith('image/');

                if (richiestaDocPreviewUrl && isPdf) {
                  return (
                    <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                      <iframe
                        title={richiestaDocPreviewRow.name || 'Anteprima documento'}
                        src={richiestaDocPreviewUrl}
                        style={{ border: 0, width: '100%', height: '100%' }}
                      />
                    </Box>
                  );
                }

                if (richiestaDocPreviewUrl && isImage) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={richiestaDocPreviewUrl}
                        alt={richiestaDocPreviewRow.name || 'Documento'}
                        style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 8 }}
                      />
                    </Box>
                  );
                }

                return (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Anteprima non disponibile per questo tipo di file.
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {richiestaDocPreviewRow && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dettagli
                </Typography>
                <Typography variant="body2">Nome: {richiestaDocPreviewRow.name || '-'}</Typography>
                <Typography variant="body2">Tipo: {richiestaDocTypeLabel(richiestaDocPreviewRow.type)}</Typography>
                <Typography variant="body2">
                  Formato: {String(richiestaDocPreviewRow.mimeType || '').includes('pdf') ? 'PDF' : String(richiestaDocPreviewRow.mimeType || '').startsWith('image/') ? 'Immagine' : 'Altro'}
                </Typography>
                <Typography variant="body2">Dimensione: {formatFileSize(richiestaDocPreviewRow.sizeBytes)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={handleClosePreview}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Anteprima documento
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExternalLink size={14} />}
                onClick={handleOpenPreviewInNewTab}
                disabled={!previewRow || openInNewTabLoading}
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 2,
                  borderRadius: '12px',
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                {openInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleClosePreview}
                sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
              >
                Chiudi
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              {previewLoading && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!previewLoading && previewRow && (() => {
                const mime = String(previewRow.mime_type || '').toLowerCase();
                const isPdf = mime.includes('pdf');
                const isImage = mime.startsWith('image/');

                if (previewUrl && isPdf) {
                  return (
                    <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                      <iframe
                        title={previewRow.original_name || 'Anteprima documento'}
                        src={previewUrl}
                        style={{ border: 0, width: '100%', height: '100%' }}
                      />
                    </Box>
                  );
                }

                if (previewUrl && isImage) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={previewUrl}
                        alt={previewRow.original_name || 'Documento'}
                        style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 8 }}
                      />
                    </Box>
                  );
                }

                return (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Anteprima non disponibile per questo tipo di file.
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {previewRow && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dettagli
                </Typography>
                <Typography variant="body2">Nome: {previewRow.original_name || '-'}</Typography>
                <Typography variant="body2">
                  Tipo: {previewRow.mime_type?.includes('pdf') ? 'PDF' : previewRow.mime_type?.startsWith('image/') ? 'Immagine' : 'Altro'}
                </Typography>
                <Typography variant="body2">
                  Data: {previewRow.created_at ? new Date(previewRow.created_at).toLocaleDateString('it-IT') : '-'}
                </Typography>
                <Typography variant="body2">
                  Dimensione: {previewRow.size_bytes ? formatFileSize(previewRow.size_bytes) : '-'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

    </Box>
  );
}
